import React, { useState, useMemo, useRef } from "react";
import { 
  Calculator, 
  Printer, 
  Copy, 
  Mail, 
  Save, 
  Trash2, 
  CheckCircle2, 
  AlertCircle, 
  FileText, 
  HelpCircle, 
  DollarSign, 
  Users, 
  User, 
  Briefcase, 
  HeartHandshake, 
  Plane, 
  Stethoscope, 
  GraduationCap, 
  Sparkles,
  ChevronDown,
  RefreshCw,
  Eye,
  Download,
  Send,
  X,
  ExternalLink,
  Check,
  ShieldCheck,
  CreditCard,
  Tag,
  BadgePercent,
  Gift,
  Lock,
  Building2,
  Shield,
  Receipt,
  ReceiptText,
  Languages,
  Landmark,
  FileCheck
} from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { DOC_TRANSLATIONS, generateDocumentSummaryText } from "./documentTranslations";

// Official IRCC Settlement Funds Requirements (updated July 2025/2026)
const IRCC_SETTLEMENT_FUNDS_CAD = {
  1: 15263,
  2: 19001,
  3: 23360,
  4: 28362,
  5: 32168,
  6: 36280,
  7: 40392,
  per_extra: 4112,
};

// Official JNIC Work Contract Tiers & Family Composition Rules
export const getWorkContractDefaultFees = (hasSpouse, childrenCount) => {
  const kids = Math.max(0, parseInt(childrenCount, 10) || 0);
  const isCouple = Boolean(hasSpouse);

  if (isCouple && kids === 1) {
    // Un couple avec 1 enfant : 7 000 000 XAF (6 000 000 traitement + 1 000 000 preuve de fond)
    return {
      processing: 6000000,
      proofFunds: 1000000,
      total: 7000000,
      tierKey: "couple_1_child",
      labelFr: "Couple avec 1 enfant (7 000 000 FCFA)",
      labelEn: "Couple with 1 child (7,000,000 FCFA)",
      ruleNoticeFr: "Formule officielle : 6 000 000 FCFA pour le traitement + 1 000 000 FCFA pour la preuve de fonds = 7 000 000 FCFA.",
      ruleNoticeEn: "Official rate: 6,000,000 FCFA processing + 1,000,000 FCFA proof of funds assistance = 7,000,000 FCFA.",
    };
  }

  if (kids >= 2) {
    // 2 enfants ou plus : 8 000 000 + 1 000 000 = 9 000 000 XAF
    return {
      processing: 8000000,
      proofFunds: 1000000,
      total: 9000000,
      tierKey: "family_2_plus",
      labelFr: `Famille avec ${kids} enfants (9 000 000 FCFA)`,
      labelEn: `Family with ${kids} children (9,000,000 FCFA)`,
      ruleNoticeFr: `Formule officielle (2 enfants ou plus) : 8 000 000 FCFA pour le traitement + 1 000 000 FCFA pour la preuve de fonds = 9 000 000 FCFA.`,
      ruleNoticeEn: `Official rate (2+ children): 8,000,000 FCFA processing + 1,000,000 FCFA proof of funds assistance = 9,000,000 FCFA.`,
    };
  }

  if (isCouple && kids === 0) {
    // Couple sans enfant : 6 000 000 XAF (5,5M traitement + 500k preuve de fond)
    return {
      processing: 5500000,
      proofFunds: 500000,
      total: 6000000,
      tierKey: "couple_0",
      labelFr: "Couple sans enfant (6 000 000 FCFA)",
      labelEn: "Couple without children (6,000,000 FCFA)",
      ruleNoticeFr: "Formule : 5 500 000 FCFA pour le traitement + 500 000 FCFA pour la preuve de fonds = 6 000 000 FCFA.",
      ruleNoticeEn: "Rate: 5,500,000 FCFA processing + 500,000 FCFA proof of funds assistance = 6,000,000 FCFA.",
    };
  }

  if (!isCouple && kids === 1) {
    // Parent seul avec 1 enfant : 6 500 000 XAF (5,5M traitement + 1M preuve de fond)
    return {
      processing: 5500000,
      proofFunds: 1000000,
      total: 6500000,
      tierKey: "single_parent_1",
      labelFr: "Parent seul avec 1 enfant (6 500 000 FCFA)",
      labelEn: "Single parent with 1 child (6,500,000 FCFA)",
      ruleNoticeFr: "Formule parent seul avec 1 enfant : 5 500 000 FCFA traitement + 1 000 000 FCFA preuve de fonds.",
      ruleNoticeEn: "Single parent with 1 child rate: 5,500,000 FCFA processing + 1,000,000 FCFA proof of funds.",
    };
  }

  // Candidat seul (défaut) : 5 000 000 XAF (4,5M traitement + 500k preuve de fond)
  return {
    processing: 4500000,
    proofFunds: 500000,
    total: 5000000,
    tierKey: "single",
    labelFr: "Candidat seul (5 000 000 FCFA)",
    labelEn: "Single applicant (5,000,000 FCFA)",
    ruleNoticeFr: "Formule standard : 4 500 000 FCFA pour le traitement + 500 000 FCFA pour la preuve de fonds = 5 000 000 FCFA.",
    ruleNoticeEn: "Standard rate: 4,500,000 FCFA processing + 500,000 FCFA proof of funds assistance = 5,000,000 FCFA.",
  };
};

export default function BudgetEstimator({ onOpenMessage }) {
  const { lang: appLang } = useLang();

  // Document language (can be French or English independently)
  const [docLang, setDocLang] = useState(() => {
    try {
      return localStorage.getItem("jnic_doc_lang") || (appLang === "en" ? "en" : "fr");
    } catch {
      return appLang === "en" ? "en" : "fr";
    }
  });

  // Document Type: 'estimate' | 'receipt' | 'invoice'
  const [docType, setDocType] = useState("estimate");

  // Form State
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    country_residence: "Cameroun",
    procedure_type: "express_entry_single", // 'express_entry_single' | 'express_entry_family' | 'work_contract'
    marital_status: "single", // 'single' | 'married' | 'common_law'
    spouse_name: "",
    has_spouse: false,
    spouse_takes_language_test: false,
    spouse_does_wes: false,
    children_count: 0,
    children_0_10: 0,
    children_11_14: 0,
    children_15_plus: 0,
    
    // Currency rates (FCFA per 1 foreign currency)
    cad_rate: 445,
    usd_rate: 610,
    eur_rate: 656,
    
    // Custom adjustable costs
    language_test_unit_cost: 226000,
    wes_evaluation_cost: 130000,
    university_process_cost: 100000,
    
    // Options toggles
    include_work_exp_assistance: false,
    include_funds_proof_assistance: false,
    include_contingency: true,
    include_post_arrival_option: false,

    // Contrat de travail spécifique (Procédure en compte bloqué)
    work_contract_processing_fee: 4500000,
    work_contract_proof_funds_fee: 500000,
    work_contract_company_sector: "Entreprise de fabrication de papier nylon",
    work_contract_payment_mode: "compte_bloque",
    work_contract_max_duration_months: 6,
    work_contract_refund_guarantee: "Si la procédure n'aboutit pas, le client est entièrement remboursé. Le blocage des fonds dure 6 mois maximum (durée de la procédure) : si ce délai est dépassé sans aboutissement, le client est également entièrement remboursé.",

    // Receipt & Payment fields
    receipt_number: `REC-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    payment_method: "compte_bloque", // 'compte_bloque' | 'virement' | 'especes' | 'carte' | 'cheque'
    payment_status: "fully_paid", // 'fully_paid' | 'partial' | 'pending'
    amount_paid: 5000000,
    payment_date: new Date().toISOString().slice(0, 10),
    payment_reference: "",

    // Rabais & Remise commerciale
    include_discount: false,
    discount_amount: 50000,
    discount_reason: "Remise promotionnelle",
    
    // Custom notes & reference
    estimate_id: `DEV-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    date: new Date().toISOString().slice(0, 10),
    validity_days: 30,
    custom_notes: "",
  });

  const [savedEstimates, setSavedEstimates] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copiedSuccess, setCopiedSuccess] = useState(false);
  const [viewMode, setViewMode] = useState("split"); // 'split' | 'preview'
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: "",
  });

  const printRef = useRef(null);

  // Sync docLang to localStorage
  const handleSetDocLang = (newLang) => {
    setDocLang(newLang);
    try {
      localStorage.setItem("jnic_doc_lang", newLang);
    } catch {}
  };

  // Sync docLang when global appLang changes
  React.useEffect(() => {
    if (appLang && (appLang === "en" || appLang === "fr")) {
      setDocLang(appLang);
    }
  }, [appLang]);

  // Load saved estimates on mount
  React.useEffect(() => {
    fetchSavedEstimates();
  }, []);

  const fetchSavedEstimates = async () => {
    setLoadingSaved(true);
    try {
      const res = await api.get("/admin/estimates");
      setSavedEstimates(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error loading estimates:", e);
    } finally {
      setLoadingSaved(false);
    }
  };

  // Helper when changing procedure or marital status
  const handleProcedureChange = (type) => {
    if (type === "express_entry_single") {
      setFormData((prev) => ({
        ...prev,
        procedure_type: type,
        marital_status: "single",
        has_spouse: false,
        spouse_takes_language_test: false,
        spouse_does_wes: false,
        children_count: 0,
        children_0_10: 0,
        children_11_14: 0,
        children_15_plus: 0,
      }));
    } else if (type === "express_entry_family") {
      setFormData((prev) => ({
        ...prev,
        procedure_type: type,
        marital_status: prev.marital_status === "single" ? "married" : prev.marital_status,
        has_spouse: true,
      }));
    } else if (type === "work_contract") {
      setFormData((prev) => {
        const defaultFees = getWorkContractDefaultFees(prev.has_spouse, prev.children_count);
        return {
          ...prev,
          procedure_type: type,
          work_contract_processing_fee: defaultFees.processing,
          work_contract_proof_funds_fee: defaultFees.proofFunds,
          work_contract_company_sector: prev.work_contract_company_sector || "Entreprise de fabrication de papier nylon",
          work_contract_payment_mode: "compte_bloque",
          amount_paid: defaultFees.total,
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        procedure_type: type,
      }));
    }
  };

  const applyWorkContractPreset = (presetKey) => {
    setFormData((prev) => {
      let isCouple = false;
      let maritalStatus = "single";
      let kids = 0;

      if (presetKey === "single") {
        isCouple = false;
        maritalStatus = "single";
        kids = 0;
      } else if (presetKey === "couple_0") {
        isCouple = true;
        maritalStatus = "married";
        kids = 0;
      } else if (presetKey === "couple_1_child") {
        isCouple = true;
        maritalStatus = "married";
        kids = 1;
      } else if (presetKey === "family_2_plus") {
        isCouple = true;
        maritalStatus = "married";
        kids = Math.max(2, prev.children_count || 2);
      }

      const fees = getWorkContractDefaultFees(isCouple, kids);

      return {
        ...prev,
        procedure_type: "work_contract",
        marital_status: maritalStatus,
        has_spouse: isCouple,
        children_count: kids,
        children_0_10: kids,
        children_11_14: 0,
        children_15_plus: 0,
        work_contract_processing_fee: fees.processing,
        work_contract_proof_funds_fee: fees.proofFunds,
        amount_paid: fees.total,
      };
    });
  };

  const handleMaritalStatusChange = (status) => {
    const isCouple = status === "married" || status === "common_law";
    setFormData((prev) => {
      const updated = {
        ...prev,
        marital_status: status,
        has_spouse: isCouple,
        procedure_type: isCouple && prev.procedure_type === "express_entry_single" ? "express_entry_family" : prev.procedure_type,
      };

      if (prev.procedure_type === "work_contract") {
        const fees = getWorkContractDefaultFees(isCouple, prev.children_count);
        updated.work_contract_processing_fee = fees.processing;
        updated.work_contract_proof_funds_fee = fees.proofFunds;
        updated.amount_paid = fees.total;
      }

      return updated;
    });
  };

  const handleChildrenCountChange = (count) => {
    const num = Math.max(0, parseInt(count, 10) || 0);
    setFormData((prev) => {
      const updated = {
        ...prev,
        children_count: num,
        children_0_10: num,
        children_11_14: 0,
        children_15_plus: 0,
      };

      if (prev.procedure_type === "work_contract") {
        const fees = getWorkContractDefaultFees(prev.has_spouse, num);
        updated.work_contract_processing_fee = fees.processing;
        updated.work_contract_proof_funds_fee = fees.proofFunds;
        updated.amount_paid = fees.total;
      }

      return updated;
    });
  };

  const updateChildAgeDistribution = (key, val) => {
    const n = Math.max(0, parseInt(val, 10) || 0);
    setFormData((prev) => {
      const updated = { ...prev, [key]: n };
      const total = (updated.children_0_10 || 0) + (updated.children_11_14 || 0) + (updated.children_15_plus || 0);
      return {
        ...updated,
        children_count: total,
      };
    });
  };

  // --- CALCULATION LOGIC ---
  const calculations = useMemo(() => {
    const {
      procedure_type,
      has_spouse,
      spouse_takes_language_test,
      spouse_does_wes,
      children_count,
      children_0_10,
      children_11_14,
      children_15_plus,
      cad_rate,
      usd_rate,
      language_test_unit_cost,
      wes_evaluation_cost,
      university_process_cost,
      include_work_exp_assistance,
      include_funds_proof_assistance,
      include_contingency,
      include_discount,
      discount_amount,
      discount_reason,
    } = formData;

    const isFamily = has_spouse || children_count > 0;
    const totalFamilyMembers = 1 + (has_spouse ? 1 : 0) + (children_count || 0);

    // 1. Language Test (TCF / TEF Canada)
    let numLanguageTests = 1;
    if (procedure_type === "work_contract") {
      numLanguageTests = 1;
    } else if (has_spouse && spouse_takes_language_test) {
      numLanguageTests = 2;
    }
    const languageTestsTotalFCFA = numLanguageTests * language_test_unit_cost;

    // 2. University Process (Équivalence à l'université)
    const numUniversityProcesses = 1 + (has_spouse && spouse_does_wes ? 1 : 0);
    const universityProcessTotalFCFA = numUniversityProcesses * university_process_cost;

    // 3. WES Account & Evaluation
    const numWes = 1 + (has_spouse && spouse_does_wes ? 1 : 0);
    const wesTotalFCFA = numWes * wes_evaluation_cost;

    // 4. Medical Exam (IOM / Approved Clinic Fees in USD)
    const numAdults = 1 + (has_spouse ? 1 : 0);
    const adultsMedicalUSD = numAdults * 198;
    const kids0_10USD = (children_0_10 || 0) * 110;
    const kids11_14USD = (children_11_14 || 0) * 155;
    const kids15PlusUSD = (children_15_plus || 0) * 198;
    const medicalTotalUSD = adultsMedicalUSD + kids0_10USD + kids11_14USD + kids15PlusUSD;
    const medicalTotalFCFA = Math.round(medicalTotalUSD * usd_rate);

    // 5. Official IRCC Government Fees (in CAD)
    let irccFeesCAD = 0;
    let irccFeesBreakdown = [];

    if (procedure_type === "work_contract") {
      const principalWorkCAD = 155 + 85;
      irccFeesBreakdown.push({ label: "Permis de travail (Principal) + Biométrie", cad: principalWorkCAD });
      irccFeesCAD += principalWorkCAD;

      if (has_spouse) {
        const spouseWorkCAD = 255 + 85;
        irccFeesBreakdown.push({ label: "Permis de travail ouvert (Conjoint) + Biométrie", cad: spouseWorkCAD });
        irccFeesCAD += spouseWorkCAD;
      }
      if (children_count > 0) {
        const kidsCAD = children_count * 100;
        irccFeesBreakdown.push({ label: `Fiches visiteur / études (${children_count} enfant${children_count > 1 ? "s" : ""})`, cad: kidsCAD });
        irccFeesCAD += kidsCAD;
      }
    } else {
      const principalCAD = 1590;
      irccFeesBreakdown.push({ label: "Frais RP Requérant principal (Traitement + Droit de RP)", cad: principalCAD });
      irccFeesCAD += principalCAD;

      if (has_spouse) {
        const spouseCAD = 1590;
        irccFeesBreakdown.push({ label: "Frais RP Époux / Conjoint (Traitement + Droit de RP)", cad: spouseCAD });
        irccFeesCAD += spouseCAD;
      }

      if (children_count > 0) {
        const kidsCAD = children_count * 270;
        irccFeesBreakdown.push({ label: `Frais RP Enfant(s) à charge (270 $CAN × ${children_count})`, cad: kidsCAD });
        irccFeesCAD += kidsCAD;
      }
    }
    const irccFeesTotalFCFA = Math.round(irccFeesCAD * cad_rate);

    // 6. JNIC Professional Fees
    const jnicHonorairesFCFA = isFamily || procedure_type === "express_entry_family" ? 1000000 : 750000;
    // Payment Milestones: 25% Start, 25% ITA, 50% Visa
    const jnicTranche1FCFA = Math.round(jnicHonorairesFCFA * 0.25);
    const jnicTranche2FCFA = Math.round(jnicHonorairesFCFA * 0.25);
    const jnicTranche3FCFA = Math.round(jnicHonorairesFCFA * 0.50);
    const jnicTotalVerseAvantVisaFCFA = jnicTranche1FCFA + jnicTranche2FCFA; // 50%
    const jnicRemboursementRefusFCFA = Math.round(jnicTotalVerseAvantVisaFCFA * 0.50); // 50% of amount already paid

    // 7. Official IRCC Proof of Settlement Funds
    let settlementFundsCAD = 0;
    if (procedure_type !== "work_contract") {
      if (totalFamilyMembers <= 7) {
        settlementFundsCAD = IRCC_SETTLEMENT_FUNDS_CAD[totalFamilyMembers] || 15263;
      } else {
        settlementFundsCAD = IRCC_SETTLEMENT_FUNDS_CAD[7] + (totalFamilyMembers - 7) * IRCC_SETTLEMENT_FUNDS_CAD.per_extra;
      }
    }
    const settlementFundsFCFA = Math.round(settlementFundsCAD * cad_rate);

    // Assistance for Proof of Funds (7%)
    const proofOfFundsAssistanceFCFA = include_funds_proof_assistance ? Math.round(settlementFundsFCFA * 0.07) : 0;

    // 8. Work Experience Formalization Assistance
    const workExpAssistanceFCFA = include_work_exp_assistance ? 150000 : 0;

    // 9. Contingencies / Imprévus
    const contingencyFCFA = include_contingency ? 250000 : 0;

    // 10. Post-Arrival Services
    const postArrivalFCFA = 300000;

    // WORK CONTRACT (CONTRAT DE TRAVAIL) SPECIFIC LOGIC
    const isWorkContract = procedure_type === "work_contract";
    const workContractProcessingFCFA = Math.max(0, parseInt(formData.work_contract_processing_fee, 10) || 4500000);
    const workContractProofFundsFCFA = Math.max(0, parseInt(formData.work_contract_proof_funds_fee, 10) || 500000);
    const workContractSubtotalFCFA = workContractProcessingFCFA + workContractProofFundsFCFA;

    const workContractFamilyLabelFr = has_spouse
      ? (children_count === 1
          ? "Couple avec 1 enfant (7 000 000 FCFA)"
          : children_count >= 2
          ? `Famille avec ${children_count} enfants (9 000 000 FCFA)`
          : "Couple sans enfant (6 000 000 FCFA)")
      : (children_count > 0
          ? `Parent seul avec ${children_count} enfant(s)`
          : "Candidat seul (5 000 000 FCFA)");

    const workContractFamilyLabelEn = has_spouse
      ? (children_count === 1
          ? "Couple with 1 child (7,000,000 FCFA)"
          : children_count >= 2
          ? `Family with ${children_count} children (9,000,000 FCFA)`
          : "Couple without children (6,000,000 FCFA)")
      : (children_count > 0
          ? `Single parent with ${children_count} child(ren)`
          : "Single applicant (5,000,000 FCFA)");

    // BRUT SUBTOTAL BEFORE DISCOUNT
    const subtotalFCFA = isWorkContract
      ? workContractSubtotalFCFA
      : (
          languageTestsTotalFCFA +
          universityProcessTotalFCFA +
          wesTotalFCFA +
          medicalTotalFCFA +
          irccFeesTotalFCFA +
          jnicHonorairesFCFA +
          proofOfFundsAssistanceFCFA +
          workExpAssistanceFCFA +
          contingencyFCFA
        );

    // DISCOUNT / RABAIS DEDUCTION
    const parsedDiscount = Math.max(0, parseInt(discount_amount, 10) || 0);
    const discountAmountFCFA = include_discount ? Math.min(subtotalFCFA, parsedDiscount) : 0;
    const hasDiscount = Boolean(include_discount && discountAmountFCFA > 0);

    // NET GRAND TOTAL AFTER DISCOUNT
    const grandTotalFCFA = Math.max(0, subtotalFCFA - discountAmountFCFA);

    const grandTotalCAD = Math.round(grandTotalFCFA / cad_rate);
    const grandTotalUSD = Math.round(grandTotalFCFA / usd_rate);
    const grandTotalEUR = Math.round(grandTotalFCFA / formData.eur_rate);

    const subtotalCAD = Math.round(subtotalFCFA / cad_rate);
    const discountCAD = Math.round(discountAmountFCFA / cad_rate);

    return {
      isWorkContract,
      workContractProcessingFCFA,
      workContractProofFundsFCFA,
      workContractSubtotalFCFA,
      workContractFamilyLabelFr,
      workContractFamilyLabelEn,
      workContractCompanySector: formData.work_contract_company_sector || "Entreprise de fabrication de papier nylon",
      workContractMaxDurationMonths: formData.work_contract_max_duration_months || 6,
      workContractRefundGuarantee: formData.work_contract_refund_guarantee || "Si la procédure n'aboutit pas, le client est entièrement remboursé. Le blocage des fonds dure 6 mois maximum (durée de la procédure) : si ce délai est dépassé sans aboutissement, le client est également entièrement remboursé.",
      isFamily,
      totalFamilyMembers,
      numLanguageTests,
      languageTestsTotalFCFA,
      numUniversityProcesses,
      universityProcessTotalFCFA,
      numWes,
      wesTotalFCFA,
      medicalTotalUSD,
      medicalTotalFCFA,
      adultsMedicalUSD,
      kids0_10USD,
      kids11_14USD,
      kids15PlusUSD,
      irccFeesCAD,
      irccFeesBreakdown,
      irccFeesTotalFCFA,
      jnicHonorairesFCFA,
      jnicTranche1FCFA,
      jnicTranche2FCFA,
      jnicTranche3FCFA,
      jnicTotalVerseAvantVisaFCFA,
      jnicRemboursementRefusFCFA,
      settlementFundsCAD,
      settlementFundsFCFA,
      proofOfFundsAssistanceFCFA,
      workExpAssistanceFCFA,
      contingencyFCFA,
      postArrivalFCFA,
      subtotalFCFA,
      subtotalCAD,
      discountAmountFCFA,
      discountCAD,
      discountReason: discount_reason || "Remise commerciale",
      hasDiscount,
      grandTotalFCFA,
      grandTotalCAD,
      grandTotalUSD,
      grandTotalEUR,
    };
  }, [formData]);

  const tDoc = (key, replacements = {}) => {
    let text = DOC_TRANSLATIONS[docLang]?.[key] || DOC_TRANSLATIONS.fr[key] || key;
    if (typeof text === "string" && replacements) {
      Object.keys(replacements).forEach((k) => {
        text = text.replace(new RegExp(`\\{${k}\\}`, "g"), replacements[k]);
      });
    }
    return text;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat(docLang === "en" ? "en-US" : "fr-FR").format(num || 0);
  };

  // Build Text Summary (Multilingual & Document Type aware)
  const generateTextSummary = () => {
    return generateDocumentSummaryText({
      formData,
      calculations,
      docType,
      docLang,
      formatNumber,
    });
  };

  // Save Estimate to DB
  const handleSaveEstimate = async () => {
    try {
      const payload = {
        ...formData,
        docType,
        docLang,
        calculations,
        updated_at: new Date().toISOString(),
      };
      await api.post("/admin/estimates", payload);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      fetchSavedEstimates();
    } catch (e) {
      alert("Erreur lors de la sauvegarde du document.");
    }
  };

  // Load a saved estimate
  const handleLoadEstimate = (est) => {
    setFormData({
      ...est,
    });
    if (est.docType) setDocType(est.docType);
    if (est.docLang) setDocLang(est.docLang);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Delete estimate
  const handleDeleteEstimate = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce document enregistré ?")) return;
    try {
      await api.delete(`/admin/estimates/${id}`);
      fetchSavedEstimates();
    } catch (err) {
      alert("Erreur lors de la suppression.");
    }
  };

  // Copy text summary for WhatsApp / Email
  const handleCopySummary = () => {
    const summary = generateTextSummary();
    navigator.clipboard.writeText(summary);
    setCopiedSuccess(true);
    setTimeout(() => setCopiedSuccess(false), 2500);
  };

  // Open Email Modal
  const handleOpenEmailModal = () => {
    const isEn = docLang === "en";
    const isReceipt = docType === "receipt";
    const docId = isReceipt ? (formData.receipt_number || formData.estimate_id) : formData.estimate_id;
    const defaultSubject = isEn
      ? (isReceipt
          ? `Official Payment Receipt - JNIC Canada (Ref: ${docId})`
          : `Official Immigration Budget Estimate - JNIC Canada (Ref: ${docId})`)
      : (isReceipt
          ? `Reçu de Paiement Officiel - JNIC Canada (Réf: ${docId})`
          : `Devis Estimatif Détaillé - Immigration Canada (Réf: ${docId})`);

    const defaultGreeting = isEn
      ? `Hello ${formData.client_name || ""},\n\nPlease find attached below your official ${isReceipt ? "payment receipt" : "budget estimate"} for your Canadian immigration file with JN Immigration Consulting.\n\n${generateTextSummary()}\n\nPlease feel free to contact us should you have any questions.\n\nWarm regards,\nThe JN Immigration Consulting Team\ninfo@jnic.org`
      : `Bonjour ${formData.client_name || ""},\n\nVeuillez trouver ci-dessous votre ${isReceipt ? "reçu de paiement officiel" : "budget estimatif personnalisé"} pour votre projet d'immigration vers le Canada avec l'accompagnement de JN Immigration Consulting.\n\n${generateTextSummary()}\n\nNous restons à votre entière disposition pour toute question ou pour démarrer les premières étapes de votre préparation.\n\nCordialement,\nL'équipe JN Immigration Consulting\ninfo@jnic.org`;

    setEmailForm({
      to: formData.client_email || "",
      subject: defaultSubject,
      body: defaultGreeting,
    });
    setEmailSuccess(false);
    setEmailError(null);
    setEmailModalOpen(true);
  };

  // Send Email via JNIC API (estimates get the branded PDF attached; receipts use text flow)
  const handleSendEmail = async () => {
    if (!emailForm.to || !emailForm.to.includes("@")) {
      setEmailError(docLang === "en" ? "Please enter a valid email address." : "Veuillez saisir une adresse email valide.");
      return;
    }
    setEmailSending(true);
    setEmailError(null);
    try {
      if (docType === "estimate") {
        // Save latest estimate so the attached PDF reflects current numbers
        await api.post("/admin/estimates", {
          ...formData,
          calculations,
          updated_at: new Date().toISOString(),
        });
        await api.post(`/admin/estimates/${formData.estimate_id}/send-email`, {
          to: emailForm.to,
          subject: emailForm.subject,
          body: emailForm.body,
        });
        fetchSavedEstimates();
      } else {
        const payload = new FormData();
        payload.append("to", emailForm.to);
        payload.append("subject", emailForm.subject);
        payload.append("body", emailForm.body);
        payload.append("template_key", "documents");

        await api.post("/admin/messages", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setEmailSuccess(true);
      setTimeout(() => {
        setEmailModalOpen(false);
        setEmailSuccess(false);
      }, 2500);
    } catch (err) {
      setEmailError(err.response?.data?.detail || (docLang === "en" ? "Failed to send email." : "Erreur lors de l'envoi de l'email."));
    } finally {
      setEmailSending(false);
    }
  };

  // Download branded PDF (server-generated, estimates only)
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfDownloading(true);
    try {
      await api.post("/admin/estimates", { ...formData, calculations, updated_at: new Date().toISOString() });
      const res = await api.get(`/admin/estimates/${formData.estimate_id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Devis-JNIC-${formData.estimate_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      fetchSavedEstimates();
    } catch (e) {
      alert(docLang === "en" ? "PDF generation failed." : "Erreur lors de la génération du PDF.");
    } finally {
      setPdfDownloading(false);
    }
  };

  // Open in Default Mail App (mailto)
  const handleOpenMailto = () => {
    const subject = encodeURIComponent(emailForm.subject);
    const body = encodeURIComponent(emailForm.body);
    window.open(`mailto:${emailForm.to}?subject=${subject}&body=${body}`, "_blank");
  };

  // Transfer to Messaging Center
  const handleTransferToMessagingCenter = () => {
    if (onOpenMessage) {
      onOpenMessage(
        formData.client_email,
        formData.client_name,
        `${docType === "receipt" ? "Reçu de Paiement JNIC" : "Devis Estimatif JNIC"} - (Réf: ${formData.estimate_id})`
      );
    }
  };

  // ROBUST PRINT ENGINE (Handles sandboxed iframe, popup & direct print fallback)
  const handlePrint = () => {
    const docElement = document.getElementById("printable-estimate-document");
    if (!docElement) {
      window.print();
      return;
    }

    const isEn = docLang === "en";
    const isReceipt = docType === "receipt";
    const docId = isReceipt ? (formData.receipt_number || formData.estimate_id) : formData.estimate_id;
    const docTitle = isReceipt
      ? (isEn
          ? `Official Payment Receipt JNIC - ${docId} - ${formData.client_name || "Applicant"}`
          : `Reçu de Paiement JNIC - ${docId} - ${formData.client_name || "Client"}`)
      : (isEn
          ? `Official Estimate JNIC - ${docId} - ${formData.client_name || "Applicant"}`
          : `Devis Estimatif JNIC - ${docId} - ${formData.client_name || "Client"}`);

    // Build standalone HTML with styles
    const printHtml = `
      <!DOCTYPE html>
      <html lang="${docLang}">
      <head>
        <meta charset="UTF-8">
        <title>${docTitle}</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
          body { font-family: 'Plus Jakarta Sans', sans-serif; background: #fff; color: #0F172A; padding: 24px; }
          .font-serif { font-family: 'Cinzel', serif; }
          .bg-navy { background-color: #06162A; color: #fff; }
          .text-navy { color: #06162A; }
          .text-redcanada, .text-terracotta { color: #D71920 !important; }
          .bg-redcanada, .bg-terracotta { background-color: #D71920 !important; color: #fff !important; }
          .border-redcanada, .border-terracotta { border-color: #D71920 !important; }
          .bg-cream { background-color: #F8FAFC; }
          .text-forest { color: #059669; }
          .bg-forest { background-color: #059669; }
          @page { size: A4; margin: 12mm; }
          @media print {
            .no-print { display: none !important; }
            body { padding: 0 !important; }
            #doc-container { border: none !important; box-shadow: none !important; padding: 0 !important; }
          }
        </style>
      </head>
      <body>
        <div class="no-print" style="position: fixed; top: 10px; right: 10px; z-index: 9999; display: flex; gap: 8px; background: #06162A; padding: 10px 16px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
          <button onclick="window.print()" style="background: #D71920; color: white; border: none; padding: 8px 16px; border-radius: 8px; font-weight: 600; cursor: pointer; font-size: 13px;">
            🍁 ${isEn ? "Print / Save as PDF (Ctrl+P)" : "Imprimer / Enregistrer en PDF (Ctrl+P)"}
          </button>
          <button onclick="window.close()" style="background: #334155; color: white; border: none; padding: 8px 14px; border-radius: 8px; font-weight: 500; cursor: pointer; font-size: 13px;">
            ${isEn ? "Close" : "Fermer"}
          </button>
        </div>

        <div id="doc-container" style="max-width: 900px; margin: 0 auto; background: white;">
          ${docElement.innerHTML}
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 600);
          };
        </script>
      </body>
      </html>
    `;

    // Try popup window first
    try {
      const printWindow = window.open("", "_blank", "width=950,height=900,menubar=no,toolbar=no,location=no,status=no");
      if (printWindow && !printWindow.closed) {
        printWindow.document.open();
        printWindow.document.write(printHtml);
        printWindow.document.close();
        return;
      }
    } catch (e) {
      console.warn("Popup blocked, falling back to hidden iframe print", e);
    }

    // Fallback: Hidden iframe printing
    try {
      let printFrame = document.getElementById("jnic-print-iframe");
      if (!printFrame) {
        printFrame = document.createElement("iframe");
        printFrame.id = "jnic-print-iframe";
        printFrame.style.position = "fixed";
        printFrame.style.right = "0";
        printFrame.style.bottom = "0";
        printFrame.style.width = "0";
        printFrame.style.height = "0";
        printFrame.style.border = "0";
        document.body.appendChild(printFrame);
      }

      const frameDoc = printFrame.contentWindow.document;
      frameDoc.open();
      frameDoc.write(printHtml);
      frameDoc.close();

      setTimeout(() => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
        } catch (err) {
          window.print();
        }
      }, 700);
    } catch (err) {
      window.print();
    }
  };

  // Download Standalone HTML Devis or Receipt
  const handleDownloadHtml = () => {
    const docElement = document.getElementById("printable-estimate-document");
    if (!docElement) return;

    const isEn = docLang === "en";
    const isReceipt = docType === "receipt";
    const docId = isReceipt ? (formData.receipt_number || formData.estimate_id) : formData.estimate_id;
    const docTitle = isReceipt
      ? (isEn
          ? `Official Payment Receipt JNIC - ${docId} - ${formData.client_name || "Applicant"}`
          : `Reçu de Paiement JNIC - ${docId} - ${formData.client_name || "Client"}`)
      : (isEn
          ? `Official Estimate JNIC - ${docId} - ${formData.client_name || "Applicant"}`
          : `Devis Estimatif JNIC - ${docId} - ${formData.client_name || "Client"}`);

    const standaloneHtml = `<!DOCTYPE html>
<html lang="${docLang}">
<head>
  <meta charset="UTF-8">
  <title>${docTitle}</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
    body { font-family: 'Plus Jakarta Sans', sans-serif; background: #f8fafc; color: #06162A; padding: 30px 15px; }
    .font-serif { font-family: 'Cinzel', serif; }
    .bg-navy { background-color: #06162A; color: #fff; }
    .text-navy { color: #06162A; }
    .text-redcanada, .text-terracotta { color: #D71920 !important; }
    .bg-redcanada, .bg-terracotta { background-color: #D71920 !important; color: #fff !important; }
    .border-redcanada, .border-terracotta { border-color: #D71920 !important; }
    @page { size: A4; margin: 10mm; }
    @media print {
      body { background: white; padding: 0; }
      .no-print { display: none !important; }
      .printable-card { box-shadow: none !important; border: none !important; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="max-width: 850px; margin: 0 auto 20px; display: flex; justify-content: space-between; align-items: center; background: #06162A; color: white; padding: 14px 20px; border-radius: 12px;">
    <div>
      <p style="font-weight: bold; margin: 0; font-size: 14px;">JN Immigration Consulting · ${isReceipt ? (isEn ? "Payment Receipt" : "Reçu de Paiement") : (isEn ? "Official Estimate" : "Devis Estimatif")}</p>
      <p style="margin: 2px 0 0; font-size: 12px; opacity: 0.7;">${isReceipt ? (isEn ? "Receipt No:" : "Reçu N° :") : (isEn ? "Ref:" : "Réf :")} ${docId} | ${formData.client_name || (isEn ? "Applicant" : "Client")}</p>
    </div>
    <button onclick="window.print()" style="background: #D71920; color: white; border: none; padding: 8px 18px; border-radius: 8px; font-weight: bold; cursor: pointer; font-size: 13px;">
      🍁 ${isEn ? "Print / Save PDF" : "Imprimer / Sauvegarder en PDF"}
    </button>
  </div>
  <div class="printable-card" style="max-width: 850px; margin: 0 auto; background: white; border-radius: 16px; border: 1px solid #e2e8f0; padding: 40px; box-shadow: 0 4px 20px rgba(0,0,0,0.05);">
    ${docElement.innerHTML}
  </div>
</body>
</html>`;

    const blob = new Blob([standaloneHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${isReceipt ? "Recu-Paiement" : "Devis-Estimatif"}-JNIC-${docId}-${(formData.client_name || "Client").replace(/\s+/g, "_")}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="budget-estimator-root">
      {/* HEADER CONTROLS */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-navy/8 shadow-sm">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-redcanada/10 flex items-center justify-center text-redcanada">
              {docType === "receipt" ? <Receipt size={20} /> : <Calculator size={20} />}
            </div>
            <div>
              <h2 className="font-serif text-2xl text-navy">
                {docLang === "en"
                  ? (docType === "receipt" ? "Official Payment Receipt Generator" : "Official Estimates & Receipts Generator")
                  : (docType === "receipt" ? "Générateur de Reçus Officiels de Paiement" : "Générateur de Devis, Reçus & Budget Estimatif")}
              </h2>
              <p className="text-xs text-navy/60">
                {docLang === "en"
                  ? "Tailored Canadian immigration estimates and official payment receipts in English & French."
                  : "Édition bilingue sur mesure pour Personne Seule, Couple/Famille, Contrats de travail et Reçus."}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* SÉLECTEUR DE TYPE DE DOCUMENT */}
          <div className="flex items-center bg-cream/80 p-1 rounded-xl border border-navy/10 text-xs">
            <button
              type="button"
              onClick={() => setDocType("estimate")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition ${
                docType === "estimate"
                  ? "bg-navy text-alabaster shadow-xs"
                  : "text-navy/70 hover:text-navy hover:bg-white/50"
              }`}
            >
              <FileText size={13} />
              <span>{docLang === "en" ? "Estimate & Quote" : "Devis Estimatif"}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setDocType("receipt");
                if (!formData.amount_paid) {
                  setFormData((prev) => ({ ...prev, amount_paid: calculations.grandTotalFCFA }));
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition ${
                docType === "receipt"
                  ? "bg-emerald-700 text-white shadow-xs font-semibold"
                  : "text-navy/70 hover:text-navy hover:bg-white/50"
              }`}
            >
              <Receipt size={13} />
              <span>{docLang === "en" ? "Official Receipt" : "Reçu de Paiement"}</span>
            </button>
            <button
              type="button"
              onClick={() => setDocType("invoice")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition ${
                docType === "invoice"
                  ? "bg-navy text-alabaster shadow-xs"
                  : "text-navy/70 hover:text-navy hover:bg-white/50"
              }`}
            >
              <CreditCard size={13} />
              <span>{docLang === "en" ? "Invoice" : "Facture"}</span>
            </button>
          </div>

          {/* SÉLECTEUR DE LANGUE DU DOCUMENT */}
          <div className="flex items-center bg-cream/80 p-1 rounded-xl border border-navy/10 text-xs">
            <span className="text-[11px] font-medium text-navy/50 px-2 flex items-center gap-1 select-none">
              <Languages size={13} />
              {docLang === "en" ? "Doc:" : "Doc :"}
            </span>
            <button
              type="button"
              onClick={() => handleSetDocLang("fr")}
              className={`px-2.5 py-1.5 rounded-lg font-semibold transition ${
                docLang === "fr"
                  ? "bg-white text-navy shadow-xs border border-navy/10"
                  : "text-navy/60 hover:text-navy"
              }`}
              title="Générer en Français"
            >
              🇫🇷 Français
            </button>
            <button
              type="button"
              onClick={() => handleSetDocLang("en")}
              className={`px-2.5 py-1.5 rounded-lg font-semibold transition ${
                docLang === "en"
                  ? "bg-navy text-white shadow-xs"
                  : "text-navy/60 hover:text-navy"
              }`}
              title="Generate in English"
            >
              🇬🇧 English
            </button>
          </div>

          <button
            type="button"
            onClick={() => setViewMode(viewMode === "split" ? "preview" : "split")}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-navy/15 text-navy text-xs font-medium hover:bg-cream transition"
          >
            <Eye size={14} />
            {viewMode === "split" ? (docLang === "en" ? "Full Preview" : "Aperçu Plein Écran") : (docLang === "en" ? "Edit Mode" : "Mode Édition")}
          </button>

          <button
            type="button"
            onClick={handleCopySummary}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-navy/15 text-navy text-xs font-medium hover:bg-cream transition"
          >
            {copiedSuccess ? <CheckCircle2 size={14} className="text-forest" /> : <Copy size={14} />}
            {copiedSuccess ? (docLang === "en" ? "Copied!" : "Copié !") : (docLang === "en" ? "Copy Text" : "Copier")}
          </button>

          <button
            type="button"
            onClick={handleSaveEstimate}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-navy text-alabaster text-xs font-medium hover:bg-redcanada transition"
          >
            {saveSuccess ? <CheckCircle2 size={14} /> : <Save size={14} />}
            {saveSuccess ? (docLang === "en" ? "Saved!" : "Enregistré !") : (docLang === "en" ? "Save" : "Sauvegarder")}
          </button>

          {/* EMAIL BUTTON */}
          <button
            type="button"
            onClick={handleOpenEmailModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition shadow-sm"
          >
            <Mail size={14} />
            {docLang === "en" ? "Send by Email" : "Envoyer par Email"}
          </button>

          {/* PRINT / PDF BUTTON */}
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-redcanada text-alabaster text-xs font-medium hover:bg-navy transition shadow-sm"
          >
            <Printer size={14} />
            {docLang === "en" ? "Print / PDF" : "Imprimer / PDF"}
          </button>

          {/* DOWNLOAD BRANDED PDF (estimates only) */}
          {docType === "estimate" && (
            <button
              type="button"
              onClick={handleDownloadPdf}
              data-testid="estimate-download-pdf-btn"
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-navy/15 text-navy text-xs font-medium hover:bg-cream transition"
              title={docLang === "en" ? "Download JNIC branded PDF" : "Télécharger le devis PDF brandé JNIC"}
            >
              {pdfDownloading ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
              PDF
            </button>
          )}

          {/* DOWNLOAD HTML */}
          <button
            type="button"
            onClick={handleDownloadHtml}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-navy/15 text-navy text-xs font-medium hover:bg-cream transition"
            title={docLang === "en" ? "Download printable standalone HTML" : "Télécharger fichier HTML prêt à imprimer"}
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div className={`grid ${viewMode === "split" ? "grid-cols-1 lg:grid-cols-12" : "grid-cols-1"} gap-8`}>
        
        {/* FORM INPUTS COLUMN */}
        {viewMode === "split" && (
          <div className="lg:col-span-5 space-y-6">

            {/* REÇU DE PAIEMENT : CONFIGURATION DU PAIEMENT (Si docType === 'receipt') */}
            {docType === "receipt" && (
              <div className="bg-emerald-50/70 p-6 rounded-2xl border-2 border-emerald-300 space-y-4 shadow-sm">
                <div className="flex items-center justify-between border-b border-emerald-200 pb-3">
                  <h3 className="font-serif text-lg text-emerald-950 flex items-center gap-2 font-bold">
                    <Receipt size={18} className="text-emerald-700" />
                    {docLang === "en" ? "Payment Receipt Settings" : "Paramètres du Reçu de Paiement"}
                  </h3>
                  <span className="text-[10px] font-mono uppercase bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-md font-bold border border-emerald-300">
                    {formData.receipt_number || formData.estimate_id}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-emerald-950 mb-1">
                        {docLang === "en" ? "Receipt Number" : "Numéro de Reçu *"}
                      </label>
                      <input
                        type="text"
                        value={formData.receipt_number}
                        onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                        className="w-full px-3 py-2 text-xs font-mono font-bold rounded-xl border border-emerald-300 bg-white focus:outline-none focus:border-emerald-600 text-navy"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-emerald-950 mb-1">
                        {docLang === "en" ? "Payment Date" : "Date de Paiement"}
                      </label>
                      <input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                        className="w-full px-3 py-2 text-xs rounded-xl border border-emerald-300 bg-white focus:outline-none focus:border-emerald-600 text-navy"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-emerald-950 mb-1">
                        {docLang === "en" ? "Payment Method" : "Mode de règlement"}
                      </label>
                      <select
                        value={formData.payment_method}
                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                        className="w-full px-3 py-2 text-xs rounded-xl border border-emerald-300 bg-white focus:outline-none focus:border-emerald-600 text-navy"
                      >
                        <option value="compte_bloque">{docLang === "en" ? "🔒 Blocked Escrow Account" : "🔒 Compte bloqué (Fonds consignés)"}</option>
                        <option value="virement">{docLang === "en" ? "Bank Wire Transfer" : "Virement bancaire"}</option>
                        <option value="especes">{docLang === "en" ? "Cash (JNIC Cashier)" : "Espèces (Caisse JNIC)"}</option>
                        <option value="carte">{docLang === "en" ? "Credit / Debit Card" : "Carte bancaire (Visa / Mastercard)"}</option>
                        <option value="cheque">{docLang === "en" ? "Certified Cheque" : "Chèque certifié"}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-emerald-950 mb-1">
                        {docLang === "en" ? "Payment Status" : "Statut du paiement"}
                      </label>
                      <select
                        value={formData.payment_status}
                        onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                        className="w-full px-3 py-2 text-xs rounded-xl border border-emerald-300 bg-white focus:outline-none focus:border-emerald-600 text-navy"
                      >
                        <option value="fully_paid">{docLang === "en" ? "✅ Fully Paid (Settled in Full)" : "✅ Soldé (Paiement Intégral)"}</option>
                        <option value="partial">{docLang === "en" ? "⏳ Deposit Paid (Partial Payment)" : "⏳ Acompte Versé (Paiement Partiel)"}</option>
                        <option value="pending">{docLang === "en" ? "⏱️ Pending Bank Clearance" : "⏱️ En attente de compensation"}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-emerald-950">
                        {docLang === "en" ? "Amount Received / Paid (FCFA) *" : "Montant Encaissé / Réglé (FCFA) *"}
                      </label>
                      <span className="text-[11px] font-mono text-emerald-800">
                        {docLang === "en" ? "Balance Due: " : "Solde dû : "}
                        <strong>{formatNumber(Math.max(0, calculations.grandTotalFCFA - (Number(formData.amount_paid) || 0)))} FCFA</strong>
                      </span>
                    </div>
                    <input
                      type="number"
                      step="50000"
                      value={formData.amount_paid}
                      onChange={(e) => setFormData({ ...formData, amount_paid: Number(e.target.value) || 0 })}
                      className="w-full px-3.5 py-2 text-sm font-mono font-bold rounded-xl border border-emerald-300 bg-white focus:outline-none focus:border-emerald-600 text-navy"
                    />

                    {/* Quick fill buttons */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-[10px] text-emerald-900 font-semibold">{docLang === "en" ? "Quick set:" : "Raccourcis :"}</span>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, amount_paid: calculations.grandTotalFCFA, payment_status: "fully_paid" })}
                        className="text-[10px] px-2 py-1 rounded bg-emerald-200 text-emerald-950 font-bold hover:bg-emerald-300 transition"
                      >
                        {docLang === "en" ? "100% Fully Settled" : "100% Soldé"} ({formatNumber(calculations.grandTotalFCFA)})
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, amount_paid: Math.round(calculations.grandTotalFCFA * 0.5), payment_status: "partial" })}
                        className="text-[10px] px-2 py-1 rounded bg-white text-emerald-900 border border-emerald-300 font-medium hover:bg-emerald-100 transition"
                      >
                        50% Acompte ({formatNumber(Math.round(calculations.grandTotalFCFA * 0.5))})
                      </button>
                      {calculations.isWorkContract ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, amount_paid: 4500000, payment_status: "partial" })}
                            className="text-[10px] px-2 py-1 rounded bg-white text-emerald-900 border border-emerald-300 font-medium hover:bg-emerald-100 transition"
                          >
                            Poste 1 (4,5M)
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, amount_paid: 500000, payment_status: "partial" })}
                            className="text-[10px] px-2 py-1 rounded bg-white text-emerald-900 border border-emerald-300 font-medium hover:bg-emerald-100 transition"
                          >
                            Poste 2 (500k)
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, amount_paid: calculations.jnicTranche1FCFA, payment_status: "partial" })}
                          className="text-[10px] px-2 py-1 rounded bg-white text-emerald-900 border border-emerald-300 font-medium hover:bg-emerald-100 transition"
                        >
                          Tranche 1 ({formatNumber(calculations.jnicTranche1FCFA)})
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-emerald-950 mb-1">
                      {docLang === "en" ? "Bank / Transaction Reference (Optional)" : "Référence Bancaire / N° Transaction (Facultatif)"}
                    </label>
                    <input
                      type="text"
                      value={formData.payment_reference}
                      onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                      placeholder={docLang === "en" ? "e.g. VIR-2026-0927-BC-89" : "Ex: VIR-2026-0927-BC-89"}
                      className="w-full px-3 py-2 text-xs rounded-xl border border-emerald-300 bg-white focus:outline-none focus:border-emerald-600 text-navy"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* 1. INFORMATIONS DU DOSSIER */}
            <div className="bg-white p-6 rounded-2xl border border-navy/8 space-y-4 shadow-sm">
              <div className="flex items-center justify-between border-b border-navy/8 pb-3">
                <h3 className="font-serif text-lg text-navy flex items-center gap-2">
                  <User size={16} className="text-redcanada" />
                  1. Informations du Candidat
                </h3>
                <span className="text-[10px] font-mono uppercase bg-cream px-2.5 py-1 rounded-md text-navy/70 border border-navy/10">
                  {formData.estimate_id}
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-navy/80 mb-1">Nom complet du requérant principal *</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Ex: Jean Dupont"
                    className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 focus:outline-none focus:border-redcanada"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-navy/80 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      placeholder="client@email.com"
                      className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 focus:outline-none focus:border-redcanada"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-navy/80 mb-1">Téléphone</label>
                    <input
                      type="text"
                      value={formData.client_phone}
                      onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      placeholder="+237 699 00 00 00"
                      className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 focus:outline-none focus:border-redcanada"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-navy/80 mb-1">Pays de résidence</label>
                  <select
                    value={formData.country_residence}
                    onChange={(e) => setFormData({ ...formData, country_residence: e.target.value })}
                    className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                  >
                    <option value="Cameroun">Cameroun</option>
                    <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                    <option value="Sénégal">Sénégal</option>
                    <option value="RDC">RD Congo</option>
                    <option value="Gabon">Gabon</option>
                    <option value="France">France</option>
                    <option value="Autre">Autre pays</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 2. TYPE DE PROCÉDURE & SITUATION FAMILIALE */}
            <div className="bg-white p-6 rounded-2xl border border-navy/8 space-y-4 shadow-sm">
              <h3 className="font-serif text-lg text-navy flex items-center gap-2 border-b border-navy/8 pb-3">
                <Users size={16} className="text-redcanada" />
                2. Procédure & Situation Familiale
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-navy/80 mb-1.5">Type de procédure</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { id: "express_entry_single", title: "Entrée Express · Personne seule", sub: "Profil individuel sans personnes à charge", icon: User },
                      { id: "express_entry_family", title: "Entrée Express · Couple & Famille", sub: "Avec conjoint et/ou enfants à charge", icon: Users },
                      { id: "work_contract", title: "Contrat de travail Canada · Compte Bloqué", sub: "5M (Seul) · 7M (Couple + 1 enf.) · 9M (2 enf.+) · Billet & Contrat inclus", icon: Lock },
                    ].map((proc) => {
                      const IconComp = proc.icon;
                      const isSelected = formData.procedure_type === proc.id;
                      return (
                        <div
                          key={proc.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleProcedureChange(proc.id)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleProcedureChange(proc.id); }}
                          className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition select-none ${
                            isSelected
                              ? "border-redcanada bg-redcanada/5 text-navy ring-2 ring-redcanada shadow-xs"
                              : "border-navy/15 hover:border-navy/35 hover:bg-cream/40 bg-white"
                          }`}
                        >
                          <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition ${
                            isSelected ? "border-redcanada bg-redcanada text-white" : "border-navy/30 bg-white"
                          }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5">
                              <IconComp size={14} className={isSelected ? "text-redcanada" : "text-navy/60"} />
                              <p className={`text-xs font-bold ${isSelected ? "text-navy" : "text-navy/90"}`}>{proc.title}</p>
                              {proc.id === "work_contract" && (
                                <span className="text-[9px] font-bold uppercase bg-forest/15 text-forest border border-forest/20 px-1.5 py-0.5 rounded">
                                  Fonds Garantis
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-navy/60 mt-0.5">{proc.sub}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* DEDICATED WORK CONTRACT SETTINGS IF WORK CONTRACT */}
                {formData.procedure_type === "work_contract" ? (
                  <div className="bg-cream/60 border border-navy/15 rounded-xl p-4 space-y-4">
                    <div className="flex items-center justify-between border-b border-navy/10 pb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-navy">
                        <Lock size={14} className="text-redcanada" />
                        <span>Configuration Contrat de Travail (Compte Bloqué)</span>
                      </div>
                      <span className="text-xs font-bold bg-navy text-white px-2.5 py-1 rounded-lg">
                        Budget Global : {formatNumber(calculations.workContractSubtotalFCFA)} FCFA
                      </span>
                    </div>

                    {/* PRESETS BARÈMES RAPIDES (1-CLIC) */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold text-navy">
                          Formule & Barème Familial (1 clic) :
                        </label>
                        <span className="text-[10px] text-redcanada font-bold uppercase tracking-wider">
                          Barème Officiel JNIC
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {/* 1. Candidat seul */}
                        <button
                          type="button"
                          onClick={() => applyWorkContractPreset("single")}
                          className={`p-2.5 rounded-xl border text-left transition ${
                            !formData.has_spouse && formData.children_count === 0
                              ? "bg-navy text-white border-navy ring-1 ring-navy shadow-xs"
                              : "bg-white text-navy border-navy/15 hover:border-navy/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold flex items-center gap-1.5">
                              <User size={13} /> Candidat seul
                            </span>
                            <span className={`text-[11px] font-mono font-bold ${!formData.has_spouse && formData.children_count === 0 ? "text-gold" : "text-navy"}`}>
                              5 000 000 FCFA
                            </span>
                          </div>
                          <p className={`text-[10px] mt-0.5 ${!formData.has_spouse && formData.children_count === 0 ? "text-white/70" : "text-navy/60"}`}>
                            4,5M traitement + 500k preuve de fonds
                          </p>
                        </button>

                        {/* 2. Couple sans enfant */}
                        <button
                          type="button"
                          onClick={() => applyWorkContractPreset("couple_0")}
                          className={`p-2.5 rounded-xl border text-left transition ${
                            formData.has_spouse && formData.children_count === 0
                              ? "bg-navy text-white border-navy ring-1 ring-navy shadow-xs"
                              : "bg-white text-navy border-navy/15 hover:border-navy/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold flex items-center gap-1.5">
                              <HeartHandshake size={13} /> Couple sans enfant
                            </span>
                            <span className={`text-[11px] font-mono font-bold ${formData.has_spouse && formData.children_count === 0 ? "text-gold" : "text-navy"}`}>
                              6 000 000 FCFA
                            </span>
                          </div>
                          <p className={`text-[10px] mt-0.5 ${formData.has_spouse && formData.children_count === 0 ? "text-white/70" : "text-navy/60"}`}>
                            5,5M traitement + 500k preuve de fonds
                          </p>
                        </button>

                        {/* 3. Couple avec 1 enfant (Précision demandée) */}
                        <button
                          type="button"
                          onClick={() => applyWorkContractPreset("couple_1_child")}
                          className={`p-2.5 rounded-xl border text-left transition ${
                            formData.has_spouse && formData.children_count === 1
                              ? "bg-navy text-white border-navy ring-1 ring-navy shadow-xs"
                              : "bg-white text-navy border-navy/15 hover:border-navy/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold flex items-center gap-1.5">
                              <Users size={13} className="text-gold" /> Couple + 1 enfant
                            </span>
                            <span className={`text-[11px] font-mono font-bold ${formData.has_spouse && formData.children_count === 1 ? "text-gold" : "text-navy"}`}>
                              7 000 000 FCFA
                            </span>
                          </div>
                          <p className={`text-[10px] mt-0.5 ${formData.has_spouse && formData.children_count === 1 ? "text-white/70" : "text-navy/60"}`}>
                            6M traitement + 1M preuve de fonds
                          </p>
                        </button>

                        {/* 4. 2 enfants ou plus (Précision demandée) */}
                        <button
                          type="button"
                          onClick={() => applyWorkContractPreset("family_2_plus")}
                          className={`p-2.5 rounded-xl border text-left transition ${
                            formData.children_count >= 2
                              ? "bg-navy text-white border-navy ring-1 ring-navy shadow-xs"
                              : "bg-white text-navy border-navy/15 hover:border-navy/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold flex items-center gap-1.5">
                              <Users size={13} className="text-gold" /> 2 enfants ou plus
                            </span>
                            <span className={`text-[11px] font-mono font-bold ${formData.children_count >= 2 ? "text-gold" : "text-navy"}`}>
                              9 000 000 FCFA
                            </span>
                          </div>
                          <p className={`text-[10px] mt-0.5 ${formData.children_count >= 2 ? "text-white/70" : "text-navy/60"}`}>
                            8M traitement + 1M preuve de fonds
                          </p>
                        </button>
                      </div>
                    </div>

                    {/* SELECTEURS DÉTAILLÉS STATUT MATRIMONIAL & ENFANTS */}
                    <div className="bg-white/80 p-3.5 rounded-xl border border-navy/10 space-y-3">
                      <p className="text-xs font-bold text-navy">Composition familiale du contrat :</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-medium text-navy/80 mb-1">Statut matrimonial</label>
                          <select
                            value={formData.marital_status}
                            onChange={(e) => handleMaritalStatusChange(e.target.value)}
                            className="w-full px-3 py-1.5 text-xs rounded-lg border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                          >
                            <option value="single">Célibataire</option>
                            <option value="married">Marié(e)</option>
                            <option value="common_law">Conjoint de fait</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-medium text-navy/80 mb-1">Nombre d'enfants à charge</label>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleChildrenCountChange(Math.max(0, formData.children_count - 1))}
                              className="w-8 h-8 rounded-lg bg-navy/5 hover:bg-navy/10 text-navy font-bold flex items-center justify-center text-xs transition"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="0"
                              max="12"
                              value={formData.children_count}
                              onChange={(e) => handleChildrenCountChange(e.target.value)}
                              className="w-full px-2 py-1 text-xs rounded-lg border border-navy/15 focus:outline-none focus:border-redcanada text-center font-bold"
                            />
                            <button
                              type="button"
                              onClick={() => handleChildrenCountChange(formData.children_count + 1)}
                              className="w-8 h-8 rounded-lg bg-navy/5 hover:bg-navy/10 text-navy font-bold flex items-center justify-center text-xs transition"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Rule Applied Summary Notification */}
                      <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-950 text-xs flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                        <div>
                          {formData.has_spouse && formData.children_count === 1 && (
                            <p><strong>Couple avec 1 enfant : 7 000 000 FCFA</strong> (6 000 000 FCFA pour le traitement + 1 000 000 FCFA pour l'assistance preuve de fonds).</p>
                          )}
                          {formData.children_count >= 2 && (
                            <p><strong>{formData.children_count} enfants ou plus : 9 000 000 FCFA</strong> (8 000 000 FCFA pour le traitement + 1 000 000 FCFA pour la preuve de fonds).</p>
                          )}
                          {formData.has_spouse && formData.children_count === 0 && (
                            <p><strong>Couple sans enfant : 6 000 000 FCFA</strong> (5 500 000 FCFA traitement + 500 000 FCFA preuve de fonds).</p>
                          )}
                          {!formData.has_spouse && formData.children_count === 0 && (
                            <p><strong>Candidat seul : 5 000 000 FCFA</strong> (4 500 000 FCFA traitement + 500 000 FCFA preuve de fonds).</p>
                          )}
                          {!formData.has_spouse && formData.children_count === 1 && (
                            <p><strong>Parent seul avec 1 enfant : 6 500 000 FCFA</strong> (5 500 000 FCFA traitement + 1 000 000 FCFA preuve de fonds).</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-navy mb-1 flex items-center justify-between">
                          <span>1. Frais de traitement, EIMT & Caution employeur (FCFA)</span>
                          <span className="text-[11px] font-mono text-redcanada font-bold">{formatNumber(formData.work_contract_processing_fee)} FCFA</span>
                        </label>
                        <input
                          type="number"
                          step="50000"
                          value={formData.work_contract_processing_fee}
                          onChange={(e) => setFormData({ ...formData, work_contract_processing_fee: e.target.value })}
                          className="w-full px-3.5 py-2 text-sm font-mono font-bold rounded-xl border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                        />
                        <p className="text-[11px] text-navy/60 mt-1 leading-relaxed">
                          Inclus les frais pour l'EIMT, la caution versée à l'entreprise qui recrute ({formData.work_contract_company_sector || "Entreprise de fabrication de papier nylon"}) et le <strong>billet d'avion pour le Canada inclus</strong> (pour toute la famille).
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-navy mb-1 flex items-center justify-between">
                          <span>2. Assistance à la réalisation de la preuve de fonds (FCFA)</span>
                          <span className="text-[11px] font-mono text-navy font-bold">{formatNumber(formData.work_contract_proof_funds_fee)} FCFA</span>
                        </label>
                        <input
                          type="number"
                          step="50000"
                          value={formData.work_contract_proof_funds_fee}
                          onChange={(e) => setFormData({ ...formData, work_contract_proof_funds_fee: e.target.value })}
                          className="w-full px-3.5 py-2 text-sm font-mono font-bold rounded-xl border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                        />
                        <p className="text-[11px] text-navy/60 mt-1 leading-relaxed">
                          Accompagnement, conformité et montage du dossier bancaire de preuve de fonds familiale.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-navy mb-1">
                            Secteur / Entreprise qui recrute
                          </label>
                          <input
                            type="text"
                            value={formData.work_contract_company_sector}
                            onChange={(e) => setFormData({ ...formData, work_contract_company_sector: e.target.value })}
                            placeholder="Entreprise de fabrication de papier nylon"
                            className="w-full px-3.5 py-2 text-xs rounded-xl border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-navy mb-1">
                            Durée maximale du blocage des fonds
                          </label>
                          <select
                            value={formData.work_contract_max_duration_months}
                            onChange={(e) => setFormData({ ...formData, work_contract_max_duration_months: parseInt(e.target.value) || 6 })}
                            className="w-full px-3.5 py-2 text-xs rounded-xl border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                          >
                            <option value={6}>6 mois maximum (Durée contractuelle de la procédure)</option>
                            <option value={9}>9 mois maximum</option>
                            <option value={12}>12 mois maximum</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-navy mb-1 flex items-center gap-1.5">
                          <ShieldCheck size={13} className="text-forest" />
                          <span>Garantie de Remboursement Intégral (Si non-aboutissement ou dépassement 6 mois)</span>
                        </label>
                        <textarea
                          rows={2}
                          value={formData.work_contract_refund_guarantee}
                          onChange={(e) => setFormData({ ...formData, work_contract_refund_guarantee: e.target.value })}
                          placeholder="Si la procédure n'aboutit pas, le client est entièrement remboursé. Le blocage des fonds dure 6 mois max (durée de la procédure), le client est également remboursé."
                          className="w-full px-3.5 py-2 text-xs rounded-xl border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                        />
                        <p className="text-[11px] text-navy/60 mt-1 leading-relaxed">
                          Mention contractuelle : si la procédure n'aboutit pas, OU si le délai maximal de 6 mois est atteint sans délivrance des documents, le client est intégralement remboursé.
                        </p>
                      </div>

                      <div className="bg-white p-3 rounded-xl border border-navy/10 space-y-1.5 text-xs text-navy/80">
                        <div className="flex items-center gap-2">
                          <span className="text-forest font-bold">🔒</span>
                          <span><strong>Fonds en compte bloqué :</strong> Protégés pour une durée maximale de 6 mois.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-forest font-bold">🛡️</span>
                          <span><strong>Si la procédure n'aboutit pas :</strong> Le client est entièrement remboursé.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-forest font-bold">⏱️</span>
                          <span><strong>Dépassement du délai de 6 mois :</strong> Le client est également entièrement remboursé.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-forest font-bold">✈️</span>
                          <span><strong>Billet d'avion inclus :</strong> Vol(s) vers le Canada compris dans les frais de traitement.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-forest font-bold">📄</span>
                          <span><strong>Livrables en fin de procédure :</strong> Billet d'avion + Contrat de travail.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div>
                        <label className="block text-xs font-medium text-navy/80 mb-1">Statut matrimonial</label>
                        <select
                          value={formData.marital_status}
                          onChange={(e) => handleMaritalStatusChange(e.target.value)}
                          className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 bg-white focus:outline-none focus:border-redcanada"
                        >
                          <option value="single">Célibataire</option>
                          <option value="married">Marié(e)</option>
                          <option value="common_law">Conjoint de fait</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-navy/80 mb-1">Total Enfants à charge</label>
                        <input
                          type="number"
                          min="0"
                          max="12"
                          value={formData.children_count}
                          onChange={(e) => handleChildrenCountChange(e.target.value)}
                          className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 focus:outline-none focus:border-redcanada"
                        />
                      </div>
                    </div>

                    {/* SPOUSE OPTIONS IF COUPLE */}
                    {formData.has_spouse && (
                      <div className="bg-cream/50 p-4 rounded-xl border border-navy/8 space-y-2.5">
                        <p className="text-xs font-semibold text-navy">Options Conjoint(e)</p>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-xs text-navy/80 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.spouse_takes_language_test}
                              onChange={(e) => setFormData({ ...formData, spouse_takes_language_test: e.target.checked })}
                              className="rounded text-redcanada focus:ring-redcanada"
                            />
                            <span>Le conjoint passe aussi le test de français (+226 000 FCFA)</span>
                          </label>
                          <label className="flex items-center gap-2 text-xs text-navy/80 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.spouse_does_wes}
                              onChange={(e) => setFormData({ ...formData, spouse_does_wes: e.target.checked })}
                              className="rounded text-redcanada focus:ring-redcanada"
                            />
                            <span>Évaluation WES + Université pour le conjoint (+230 000 FCFA)</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* CHILDREN AGE BREAKDOWN FOR MEDICAL FEES */}
                    {formData.children_count > 0 && (
                      <div className="bg-cream/50 p-4 rounded-xl border border-navy/8 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-navy flex items-center gap-1.5">
                            <Stethoscope size={14} className="text-redcanada" />
                            Répartition d'âge des enfants (Visite Médicale OIM)
                          </p>
                          <span className="text-[10px] text-navy/60 font-medium">Total: {formData.children_count}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-[10px] font-medium text-navy/70 mb-1">0 à 10 ans (110 $US)</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.children_0_10}
                              onChange={(e) => updateChildAgeDistribution("children_0_10", e.target.value)}
                              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-navy/15 focus:outline-none focus:border-redcanada text-center"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-navy/70 mb-1">11 à 14 ans (155 $US)</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.children_11_14}
                              onChange={(e) => updateChildAgeDistribution("children_11_14", e.target.value)}
                              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-navy/15 focus:outline-none focus:border-redcanada text-center"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-navy/70 mb-1">15 ans et + (198 $US)</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.children_15_plus}
                              onChange={(e) => updateChildAgeDistribution("children_15_plus", e.target.value)}
                              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-navy/15 focus:outline-none focus:border-redcanada text-center"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 3. OPTIONS & ASSISTANCES SPÉCIFIQUES */}
            <div className="bg-white p-6 rounded-2xl border border-navy/8 space-y-4 shadow-sm">
              <h3 className="font-serif text-lg text-navy flex items-center gap-2 border-b border-navy/8 pb-3">
                <Sparkles size={16} className="text-redcanada" />
                3. Options & Assistances Personnalisées
              </h3>

              <div className="space-y-3">
                {/* Formalisation Exp Pro */}
                <label className="flex items-start gap-3 p-3 rounded-xl border border-navy/10 hover:bg-cream/40 cursor-pointer transition">
                  <input
                    type="checkbox"
                    checked={formData.include_work_exp_assistance}
                    onChange={(e) => setFormData({ ...formData, include_work_exp_assistance: e.target.checked })}
                    className="mt-0.5 rounded text-redcanada focus:ring-redcanada"
                  />
                  <div>
                    <p className="text-xs font-semibold text-navy">Assistance formalisation de l'expérience professionnelle</p>
                    <p className="text-[11px] text-navy/60">150 000 FCFA (Optionnel si le candidat peut justifier et formaliser lui-même ses preuves d'emploi).</p>
                  </div>
                </label>

                {/* Preuve de fonds 7% */}
                {formData.procedure_type !== "work_contract" && (
                  <label className="flex items-start gap-3 p-3 rounded-xl border border-navy/10 hover:bg-cream/40 cursor-pointer transition">
                    <input
                      type="checkbox"
                      checked={formData.include_funds_proof_assistance}
                      onChange={(e) => setFormData({ ...formData, include_funds_proof_assistance: e.target.checked })}
                      className="mt-0.5 rounded text-redcanada focus:ring-redcanada"
                    />
                    <div>
                      <p className="text-xs font-semibold text-navy">Assistance & Structuration de la preuve de fonds (7%)</p>
                      <p className="text-[11px] text-navy/60">
                        {formatNumber(calculations.proofOfFundsAssistanceFCFA)} FCFA (7% de {formatNumber(calculations.settlementFundsFCFA)} FCFA exigés par IRCC).
                      </p>
                    </div>
                  </label>
                )}

                {/* Imprévus */}
                <label className="flex items-start gap-3 p-3 rounded-xl border border-navy/10 hover:bg-cream/40 cursor-pointer transition">
                  <input
                    type="checkbox"
                    checked={formData.include_contingency}
                    onChange={(e) => setFormData({ ...formData, include_contingency: e.target.checked })}
                    className="mt-0.5 rounded text-redcanada focus:ring-redcanada"
                  />
                  <div>
                    <p className="text-xs font-semibold text-navy">Réserve pour imprévus administratifs</p>
                    <p className="text-[11px] text-navy/60">250 000 FCFA (Transport, légalisations, certifications, frais de notaire si nécessaire).</p>
                  </div>
                </label>

                {/* Services Post-Arrivée */}
                <label className="flex items-start gap-3 p-3 rounded-xl border border-forest/20 bg-forest/5 hover:bg-forest/10 cursor-pointer transition">
                  <input
                    type="checkbox"
                    checked={formData.include_post_arrival_option}
                    onChange={(e) => setFormData({ ...formData, include_post_arrival_option: e.target.checked })}
                    className="mt-0.5 rounded text-forest focus:ring-forest"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs font-semibold text-forest">Afficher l'option Services Post-Arrivée</p>
                      <span className="text-[9px] bg-forest/20 text-forest font-bold px-1.5 py-0.5 rounded">À la carte</span>
                    </div>
                    <p className="text-[11px] text-navy/70">
                      300 000 FCFA (Accueil à l'aéroport, transport, carte santé, compte bancaire, forfait tél, NAS/SIN). <em className="text-navy/50">Non inclus dans le total obligatoire</em>.
                    </p>
                  </div>
                </label>
              </div>

              {/* RECAP TRANCHES HONORAIRES & REMBOURSEMENT */}
              <div className="mt-4 pt-4 border-t border-navy/10 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-navy flex items-center gap-1.5">
                    <CreditCard size={14} className="text-redcanada" />
                    Échéancier Honoraires JNIC
                  </span>
                  <span className="text-xs font-bold text-navy font-mono">
                    {formatNumber(calculations.jnicHonorairesFCFA)} FCFA
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
                  <div className="bg-cream/60 p-2 rounded-lg border border-navy/8">
                    <p className="text-navy/60 font-medium">1ère Tranche (25%)</p>
                    <p className="font-bold text-navy font-mono text-xs mt-0.5">{formatNumber(calculations.jnicTranche1FCFA)}</p>
                    <p className="text-[9px] text-navy/50 mt-0.5">Au début</p>
                  </div>
                  <div className="bg-cream/60 p-2 rounded-lg border border-navy/8">
                    <p className="text-navy/60 font-medium">2ème Tranche (25%)</p>
                    <p className="font-bold text-navy font-mono text-xs mt-0.5">{formatNumber(calculations.jnicTranche2FCFA)}</p>
                    <p className="text-[9px] text-navy/50 mt-0.5">À l'ITA</p>
                  </div>
                  <div className="bg-cream/60 p-2 rounded-lg border border-navy/8">
                    <p className="text-navy/60 font-medium">3ème Tranche (50%)</p>
                    <p className="font-bold text-redcanada font-mono text-xs mt-0.5">{formatNumber(calculations.jnicTranche3FCFA)}</p>
                    <p className="text-[9px] text-navy/50 mt-0.5">Au Visa IRCC</p>
                  </div>
                </div>

                <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-[10px] flex items-start gap-2">
                  <ShieldCheck size={14} className="text-emerald-700 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-emerald-800">Garantie de Remboursement : </strong>
                    En cas de refus IRCC, 50% du montant déjà versé est remboursé ({formatNumber(calculations.jnicRemboursementRefusFCFA)} FCFA), car les 50% restants n'auront pas encore été versés.
                  </div>
                </div>
              </div>
            </div>

            {/* 4. SECTION RABAIS & REMISE COMMERCIALE */}
            <div className={`p-5 rounded-2xl border transition-all duration-200 shadow-sm ${formData.include_discount ? 'bg-gradient-to-br from-emerald-50/70 to-white border-emerald-300 ring-1 ring-emerald-200' : 'bg-white border-navy/8'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${formData.include_discount ? 'bg-emerald-600 text-white' : 'bg-navy/5 text-navy/70'}`}>
                    <Tag size={15} />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-navy flex items-center gap-1.5">
                      Rabais & Remise Commerciale
                      {formData.include_discount && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded">
                          - {formatNumber(calculations.discountAmountFCFA)} FCFA
                        </span>
                      )}
                    </h4>
                    <p className="text-[11px] text-navy/60">Appliquer une déduction promotionnelle ou un geste commercial sur le devis</p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.include_discount}
                    onChange={(e) => setFormData({ ...formData, include_discount: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              {formData.include_discount && (
                <div className="mt-4 pt-3 border-t border-emerald-200/60 space-y-3 animate-fade-in">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1">
                        Montant du rabais (FCFA) *
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="5000"
                          min="0"
                          max={calculations.subtotalFCFA}
                          value={formData.discount_amount}
                          onChange={(e) => setFormData({ ...formData, discount_amount: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                          placeholder="Ex: 50000"
                          className="w-full px-3.5 py-2 text-sm rounded-xl border border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-600 font-mono font-bold text-emerald-800 bg-white"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-emerald-700 font-bold">FCFA</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1">
                        Motif / Libellé de la remise
                      </label>
                      <input
                        type="text"
                        value={formData.discount_reason}
                        onChange={(e) => setFormData({ ...formData, discount_reason: e.target.value })}
                        placeholder="Ex: Remise promotionnelle, Offre parrainage..."
                        className="w-full px-3.5 py-2 text-xs rounded-xl border border-navy/15 focus:outline-none focus:border-emerald-600 bg-white"
                      />
                    </div>
                  </div>

                  {/* Boutons rapides pour sélectionner un montant */}
                  <div>
                    <span className="text-[10px] text-navy/60 font-medium block mb-1.5">Montants prédéfinis rapides :</span>
                    <div className="flex flex-wrap gap-1.5">
                      {[25000, 50000, 100000, 150000, 200000, 250000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setFormData({ ...formData, discount_amount: preset })}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-mono font-medium transition ${
                            formData.discount_amount === preset
                              ? "bg-emerald-600 text-white shadow-sm font-bold"
                              : "bg-white border border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                          }`}
                        >
                          {formatNumber(preset)} F
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Visualisation de l'impact financier */}
                  <div className="bg-emerald-100/60 rounded-xl p-3 border border-emerald-200 flex items-center justify-between text-xs">
                    <div>
                      <p className="text-emerald-900 font-semibold">Impact immédiat sur le devis :</p>
                      <p className="text-[11px] text-emerald-800">
                        Sous-total : <span className="line-through">{formatNumber(calculations.subtotalFCFA)} FCFA</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase font-bold text-emerald-800 block">Nouveau total net</span>
                      <span className="text-sm font-bold text-emerald-900 font-mono">
                        {formatNumber(calculations.grandTotalFCFA)} FCFA
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 5. TAUX DE CHANGE CONFIGURABLES */}
            <div className="bg-white p-5 rounded-2xl border border-navy/8 space-y-3 shadow-sm">
              <h4 className="text-xs font-semibold text-navy flex items-center gap-1.5">
                <DollarSign size={14} className="text-redcanada" />
                Taux de change appliqués (FCFA)
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] text-navy/60 mb-0.5">1 $CAD en FCFA</label>
                  <input
                    type="number"
                    value={formData.cad_rate}
                    onChange={(e) => setFormData({ ...formData, cad_rate: Number(e.target.value) || 445 })}
                    className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-navy/15 focus:outline-none text-center font-medium"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-navy/60 mb-0.5">1 $USD en FCFA</label>
                  <input
                    type="number"
                    value={formData.usd_rate}
                    onChange={(e) => setFormData({ ...formData, usd_rate: Number(e.target.value) || 610 })}
                    className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-navy/15 focus:outline-none text-center font-medium"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-navy/60 mb-0.5">1 € en FCFA</label>
                  <input
                    type="number"
                    value={formData.eur_rate}
                    onChange={(e) => setFormData({ ...formData, eur_rate: Number(e.target.value) || 656 })}
                    className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-navy/15 focus:outline-none text-center font-medium"
                  />
                </div>
              </div>
            </div>

            {/* SAVED ESTIMATES DRAWER / LIST */}
            <div className="bg-white p-5 rounded-2xl border border-navy/8 space-y-3 shadow-sm">
              <div className="flex items-center justify-between border-b border-navy/8 pb-2">
                <h4 className="text-xs font-semibold text-navy flex items-center gap-1.5">
                  <FileText size={14} className="text-redcanada" />
                  Devis enregistrés ({savedEstimates.length})
                </h4>
                <button
                  type="button"
                  onClick={fetchSavedEstimates}
                  className="text-navy/50 hover:text-navy p-1"
                  title="Actualiser"
                >
                  <RefreshCw size={12} className={loadingSaved ? "animate-spin" : ""} />
                </button>
              </div>

              {savedEstimates.length === 0 ? (
                <p className="text-xs text-navy/40 italic py-2">Aucun devis sauvegardé pour le moment.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                  {savedEstimates.map((est) => (
                    <div
                      key={est.estimate_id}
                      onClick={() => handleLoadEstimate(est)}
                      className="flex items-center justify-between p-2.5 rounded-xl border border-navy/8 hover:border-redcanada/40 hover:bg-cream/50 cursor-pointer transition text-xs"
                    >
                      <div>
                        <p className="font-semibold text-navy">{est.client_name || "Sans nom"} <span className="text-[10px] text-navy/50">({est.estimate_id})</span></p>
                        <p className="text-[10px] text-navy/60">{est.date} · {formatNumber(est.calculations?.grandTotalFCFA)} FCFA</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteEstimate(est.estimate_id, e)}
                        className="text-navy/30 hover:text-redcanada p-1 transition"
                        title="Supprimer"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}

        {/* PRINTABLE DOCUMENT PREVIEW COLUMN */}
        <div className={`${viewMode === "split" ? "lg:col-span-7" : "max-w-4xl mx-auto w-full"}`}>
          
          <div 
            ref={printRef}
            className="bg-white p-8 md:p-12 rounded-2xl border border-navy/10 shadow-lg print:border-none print:shadow-none print:p-0 text-navy font-sans relative"
            id="printable-estimate-document"
          >
            {/* DOCUMENT HEADER */}
            <div className="border-b-2 border-redcanada pb-6 mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-4 h-4 bg-redcanada rounded-full flex items-center justify-center text-white text-[9px] font-bold">🍁</span>
                    <h1 className="font-serif text-3xl font-bold text-navy tracking-tight">{tDoc("org_title")}</h1>
                  </div>
                  <p className="text-xs font-semibold tracking-wider text-redcanada uppercase">
                    {tDoc("org_tagline")}
                  </p>
                  <p className="text-[11px] text-navy/60 mt-0.5">
                    {tDoc("org_contact")}
                  </p>
                </div>

                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-md font-mono text-xs font-semibold tracking-widest uppercase mb-1.5 ${
                    docType === "receipt" ? "bg-emerald-700 text-white" : "bg-navy text-alabaster"
                  }`}>
                    {docType === "receipt" ? tDoc("badge_receipt") : docType === "invoice" ? tDoc("badge_invoice") : tDoc("badge_estimate")}
                  </span>
                  <p className="text-xs font-mono text-navy font-medium">
                    {docType === "receipt" ? `${tDoc("receipt_num_label")} ${formData.receipt_number || formData.estimate_id}` : `${tDoc("num_label")} ${formData.estimate_id}`}
                  </p>
                  <p className="text-[11px] text-navy/60">{tDoc("date_label")} {formData.date}</p>
                  <p className="text-[10px] text-navy/50">{tDoc("validity_label")} {formData.validity_days} {tDoc("validity_suffix")}</p>
                </div>
              </div>
            </div>

            {/* OFFICIAL PAYMENT RECEIPT HIGHLIGHT BOX (Quand docType === 'receipt') */}
            {docType === "receipt" && (
              <div className="bg-emerald-50/90 border-2 border-emerald-400 rounded-xl p-4 mb-6 shadow-xs">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-200 pb-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shadow-xs">✓</span>
                    <div>
                      <p className="text-xs uppercase font-bold text-emerald-950 tracking-wider">
                        {tDoc("badge_receipt")} · JNIC IMMIGRATION
                      </p>
                      <p className="text-[11px] text-emerald-800">
                        {tDoc("payment_certified")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 block">
                      {tDoc("amount_paid")}
                    </span>
                    <p className="text-xl font-bold font-mono text-emerald-700">
                      {formatNumber(formData.amount_paid ?? calculations.grandTotalFCFA)} FCFA
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <span className="text-[10px] uppercase text-emerald-900/60 font-semibold block">{tDoc("payment_method")}</span>
                    <p className="font-semibold text-emerald-950 mt-0.5">
                      {formData.payment_method === "compte_bloque" ? tDoc("method_compte_bloque") :
                       formData.payment_method === "virement" ? tDoc("method_virement") :
                       formData.payment_method === "especes" ? tDoc("method_especes") :
                       formData.payment_method === "carte" ? tDoc("method_carte") :
                       tDoc("method_cheque")}
                    </p>
                  </div>

                  <div>
                    <span className="text-[10px] uppercase text-emerald-900/60 font-semibold block">{tDoc("payment_status")}</span>
                    <p className="font-bold text-emerald-900 mt-0.5">
                      {formData.payment_status === "fully_paid" ? tDoc("status_fully_paid") :
                       formData.payment_status === "partial" ? tDoc("status_partial") :
                       tDoc("status_pending")}
                    </p>
                  </div>

                  <div>
                    <span className="text-[10px] uppercase text-emerald-900/60 font-semibold block">{tDoc("balance_due")}</span>
                    <p className="font-bold font-mono text-emerald-950 mt-0.5">
                      {formatNumber(Math.max(0, calculations.grandTotalFCFA - (Number(formData.amount_paid) || 0)))} FCFA
                    </p>
                  </div>

                  <div>
                    <span className="text-[10px] uppercase text-emerald-900/60 font-semibold block">{tDoc("payment_date")}</span>
                    <p className="font-medium text-emerald-950 mt-0.5">
                      {formData.payment_date || formData.date}
                    </p>
                    {formData.payment_reference && (
                      <p className="text-[10px] font-mono text-emerald-800 truncate" title={formData.payment_reference}>
                        Réf: {formData.payment_reference}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CANDIDATE & DOSSIER RECAP BANNER */}
            <div className="bg-cream/60 rounded-xl p-4 mb-6 border border-navy/8 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-navy/50 font-semibold">{tDoc("candidate_principal")}</p>
                <p className="font-bold text-navy text-sm mt-0.5">{formData.client_name || (docLang === "en" ? "Applicant" : "Client")}</p>
                {formData.client_phone && <p className="text-[11px] text-navy/60">{formData.client_phone}</p>}
                {formData.client_email && <p className="text-[11px] text-navy/60">{formData.client_email}</p>}
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-navy/50 font-semibold">{tDoc("procedure")}</p>
                <p className="font-semibold text-navy mt-0.5">
                  {formData.procedure_type === "express_entry_single" ? tDoc("proc_express_single") :
                   formData.procedure_type === "express_entry_family" ? tDoc("proc_express_family") :
                   tDoc("proc_work_contract")}
                </p>
                <p className="text-[11px] text-navy/60">{tDoc("residence")} {formData.country_residence}</p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-navy/50 font-semibold">
                  {tDoc("family_composition")}
                </p>
                <p className="font-semibold text-navy mt-0.5">
                  {calculations.isWorkContract ? (
                    <span>
                      {docLang === "en" ? calculations.workContractFamilyLabelEn : calculations.workContractFamilyLabelFr}
                    </span>
                  ) : (
                    <>
                      {formData.has_spouse ? tDoc("couple") : tDoc("single")}
                      {formData.children_count > 0 ? ` + ${formData.children_count} ${formData.children_count > 1 ? tDoc("child_plural") : tDoc("child_singular")}` : ""}
                    </>
                  )}
                </p>
                <p className="text-[11px] text-navy/60">
                  {calculations.isWorkContract ? (
                    <span className="text-forest font-semibold flex items-center gap-1">
                      <Lock size={11} className="shrink-0" />
                      {tDoc("blocked_account_tag")} · {tDoc("escrow_guaranteed")}
                    </span>
                  ) : `${tDoc("total_persons")} ${calculations.totalFamilyMembers} ${tDoc("persons_unit")}`}
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-navy/50 font-semibold">
                  {calculations.isWorkContract ? tDoc("final_deliverables") : tDoc("applied_rates")}
                </p>
                {calculations.isWorkContract ? (
                  <>
                    <p className="text-[11px] font-semibold text-redcanada mt-0.5">{tDoc("flight_included")}</p>
                    <p className="text-[11px] text-navy/70">{tDoc("work_contract_delivered")}</p>
                  </>
                ) : (
                  <>
                    <p className="text-[11px] font-mono text-navy mt-0.5">1 $CAD = {formData.cad_rate} FCFA</p>
                    <p className="text-[11px] font-mono text-navy/60">1 $USD = {formData.usd_rate} FCFA</p>
                  </>
                )}
              </div>
            </div>

            {/* EXPENSES BREAKDOWN TABLE */}
            <div className="space-y-6">
              
              {calculations.isWorkContract ? (
                /* SECTION SPÉCIFIQUE : CONTRAT DE TRAVAIL (COMPTE BLOQUÉ) */
                <div className="space-y-5">
                  {/* WORK CONTRACT BANNER */}
                  <div className="bg-navy/5 border-2 border-navy/15 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-navy text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <Lock size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-navy text-sm uppercase tracking-wide">
                            {tDoc("wc_banner_title")}
                          </h3>
                          <span className="text-[10px] font-bold bg-forest/15 text-forest px-2 py-0.5 rounded border border-forest/20">
                            {tDoc("wc_banner_tag")}
                          </span>
                        </div>
                        <p className="text-xs text-navy/70 mt-0.5">
                          {tDoc("wc_banner_desc")}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] uppercase font-semibold text-navy/50">{tDoc("wc_fixed_total")}</span>
                      <p className="text-base font-bold font-mono text-redcanada">
                        {formatNumber(calculations.subtotalFCFA)} FCFA
                      </p>
                    </div>
                  </div>

                  {/* TABLEAU DES DEUX DÉPENSES */}
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-redcanada border-b border-navy/10 pb-1.5 mb-3 flex items-center gap-1.5">
                      <Briefcase size={14} />
                      {tDoc("wc_table_title")}
                    </h3>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-navy/50 text-[10px] uppercase border-b border-navy/8">
                          <th className="text-left pb-1 font-semibold">{tDoc("col_expense")}</th>
                          <th className="text-center pb-1 font-semibold">{tDoc("col_scheme")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_inclusions")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_amount_fcfa")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-navy/5">
                        {/* ITEM 1: FRAIS DE TRAITEMENT, EIMT, CAUTION ET BILLET */}
                        <tr>
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[9px] font-bold bg-redcanada text-white px-1.5 py-0.5 rounded uppercase">
                                {tDoc("wc_item1_badge")}
                              </span>
                              <p className="font-bold text-navy text-xs">
                                {tDoc("wc_item1_title")}
                              </p>
                            </div>
                            <div className="text-[11px] text-navy/75 mt-1 leading-relaxed space-y-0.5">
                              <p>{tDoc("wc_item1_detail1")}</p>
                              <p>{tDoc("wc_item1_detail2", { sector: calculations.workContractCompanySector || (docLang === "en" ? "Nylon paper manufacturing company" : "Entreprise de fabrication de papier nylon") })}</p>
                              <p>
                                <strong>
                                  {formData.has_spouse || formData.children_count > 0
                                    ? (docLang === "en"
                                        ? "• Flight ticket(s) to Canada INCLUDED for applicant & family without extra cost."
                                        : "• Billet(s) d'avion pour le Canada INCLUS pour le demandeur et sa famille sans surcoût.")
                                    : tDoc("wc_item1_detail3")}
                                </strong>
                              </p>
                              <p>
                                {formData.has_spouse || formData.children_count > 0
                                  ? (docLang === "en"
                                      ? "• At completion, flight tickets, family visas, and official employment contract are directly handed over."
                                      : "• À la fin de la procédure, il est remis directement les billets d'avion de la famille, les visas et le contrat de travail.")
                                  : tDoc("wc_item1_detail4")}
                              </p>
                            </div>
                          </td>
                          <td className="py-3 text-center align-top">
                            <span className="text-[9px] font-bold bg-navy/10 text-navy px-2 py-0.5 rounded">
                              {tDoc("blocked_account_tag")}
                            </span>
                          </td>
                          <td className="py-3 text-right text-[11px] text-navy/70 align-top font-medium whitespace-pre-line">
                            {tDoc("wc_item1_incl")}
                          </td>
                          <td className="py-3 text-right font-mono font-bold text-navy text-sm align-top">
                            {formatNumber(calculations.workContractProcessingFCFA)}
                          </td>
                        </tr>

                        {/* ITEM 2: ASSISTANCE A LA REALISATION DE LA PREUVE DE FONDS */}
                        <tr>
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[9px] font-bold bg-navy text-white px-1.5 py-0.5 rounded uppercase">
                                {tDoc("wc_item2_badge")}
                              </span>
                              <p className="font-bold text-navy text-xs">
                                {tDoc("wc_item2_title")}
                              </p>
                            </div>
                            <p className="text-[11px] text-navy/75 mt-1 leading-relaxed">
                              {formData.has_spouse || formData.children_count > 0
                                ? (docLang === "en"
                                    ? "• Technical banking structuring and complete assembly of family proof of settlement funds required for Canadian visa issuance."
                                    : "• Accompagnement technique, structuration bancaire et montage complet de la preuve de fonds familiale requise pour la délivrance des visas.")
                                : tDoc("wc_item2_detail1")}
                            </p>
                          </td>
                          <td className="py-3 text-center align-top">
                            <span className="text-[9px] font-bold bg-navy/10 text-navy px-2 py-0.5 rounded">
                              {tDoc("blocked_account_tag")}
                            </span>
                          </td>
                          <td className="py-3 text-right text-[11px] text-navy/70 align-top font-medium whitespace-pre-line">
                            {tDoc("wc_item2_incl")}
                          </td>
                          <td className="py-3 text-right font-mono font-bold text-navy text-sm align-top">
                            {formatNumber(calculations.workContractProofFundsFCFA)}
                          </td>
                        </tr>

                        {/* LIGNE RABAIS / REMISE COMMERCIALE DÉDUITE */}
                        {calculations.hasDiscount && (
                          <tr className="bg-emerald-50/70 border-t border-b border-emerald-200">
                            <td className="py-2.5 pr-2">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] bg-emerald-600 text-white font-bold px-1.5 py-0.5 rounded uppercase">
                                  {tDoc("discount_badge")}
                                </span>
                                <p className="font-bold text-emerald-950">
                                  {formData.discount_reason || (docLang === "en" ? "Commercial Discount" : "Remise commerciale")}
                                </p>
                              </div>
                              <p className="text-[10px] text-emerald-800">{tDoc("discount_sub")}</p>
                            </td>
                            <td className="py-2.5 text-center">
                              <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                                {tDoc("discount_deducted")}
                              </span>
                            </td>
                            <td className="py-2.5 text-right text-[10px] text-emerald-800 font-medium">{tDoc("discount_courtesy")}</td>
                            <td className="py-2.5 text-right font-mono font-bold text-emerald-700">
                              - {formatNumber(calculations.discountAmountFCFA)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* PROTOCOLE COMPTE BLOQUÉ & REMISE FINALE */}
                  <div className="bg-cream/70 border border-navy/15 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-navy/10 pb-2">
                      <p className="text-xs font-bold text-navy flex items-center gap-1.5">
                        <ShieldCheck size={15} className="text-forest" />
                        {tDoc("protocol_title")}
                      </p>
                      <span className="text-[10px] font-bold text-forest bg-white border border-forest/20 px-2 py-0.5 rounded">
                        {tDoc("protocol_tag")}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs">
                      <div className="bg-white p-3 rounded-lg border border-navy/8 space-y-1">
                        <p className="font-bold text-navy flex items-center gap-1.5">
                          {tDoc("card1_title")}
                        </p>
                        <p className="text-[11px] text-navy/70 leading-snug">
                          {tDoc("card1_desc")}
                        </p>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-emerald-200 bg-emerald-50/50 space-y-1">
                        <p className="font-bold text-emerald-950 flex items-center gap-1.5">
                          {tDoc("card2_title")}
                        </p>
                        <p className="text-[11px] text-emerald-900 leading-snug">
                          {tDoc("card2_desc")}
                        </p>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-navy/8 space-y-1">
                        <p className="font-bold text-navy flex items-center gap-1.5">
                          {tDoc("card3_title")}
                        </p>
                        <p className="text-[11px] text-navy/70 leading-snug">
                          {tDoc("card3_desc")}
                        </p>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-navy/8 space-y-1">
                        <p className="font-bold text-navy flex items-center gap-1.5">
                          {tDoc("card4_title")}
                        </p>
                        <p className="text-[11px] text-navy/70 leading-snug">
                          {tDoc("card4_desc")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* SECTION 1: ÉVALUATIONS & PRÉREQUIS */}
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-redcanada border-b border-navy/10 pb-1.5 mb-3 flex items-center gap-1.5">
                      <GraduationCap size={14} />
                      {tDoc("ee_sec1_title")}
                    </h3>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-navy/50 text-[10px] uppercase border-b border-navy/8">
                          <th className="text-left pb-1 font-semibold">{tDoc("col_designation")}</th>
                          <th className="text-center pb-1 font-semibold">{tDoc("col_qty")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_unit_price")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_amount_fcfa")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-navy/5">
                        <tr>
                          <td className="py-2 pr-2">
                            <p className="font-medium text-navy">{tDoc("ee_lang_test_title")}</p>
                            <p className="text-[10px] text-navy/60">{tDoc("ee_lang_test_desc")}</p>
                          </td>
                          <td className="py-2 text-center">{calculations.numLanguageTests}</td>
                          <td className="py-2 text-right font-mono">{formatNumber(formData.language_test_unit_cost)}</td>
                          <td className="py-2 text-right font-mono font-semibold">{formatNumber(calculations.languageTestsTotalFCFA)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-2">
                            <p className="font-medium text-navy">{tDoc("ee_univ_title")}</p>
                            <p className="text-[10px] text-navy/60">{tDoc("ee_univ_desc")}</p>
                          </td>
                          <td className="py-2 text-center">{calculations.numUniversityProcesses}</td>
                          <td className="py-2 text-right font-mono">{formatNumber(formData.university_process_cost)}</td>
                          <td className="py-2 text-right font-mono font-semibold">{formatNumber(calculations.universityProcessTotalFCFA)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-2">
                            <p className="font-medium text-navy">{tDoc("ee_wes_title")}</p>
                            <p className="text-[10px] text-navy/60">{tDoc("ee_wes_desc")}</p>
                          </td>
                          <td className="py-2 text-center">{calculations.numWes}</td>
                          <td className="py-2 text-right font-mono">{formatNumber(formData.wes_evaluation_cost)}</td>
                          <td className="py-2 text-right font-mono font-semibold">{formatNumber(calculations.wesTotalFCFA)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* SECTION 2: FRAIS GOUVERNEMENTAUX & MÉDICAUX */}
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-redcanada border-b border-navy/10 pb-1.5 mb-3 flex items-center gap-1.5">
                      <Stethoscope size={14} />
                      {tDoc("ee_sec2_title")}
                    </h3>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-navy/50 text-[10px] uppercase border-b border-navy/8">
                          <th className="text-left pb-1 font-semibold">{tDoc("col_designation")}</th>
                          <th className="text-center pb-1 font-semibold">{tDoc("col_currency_orig")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_equiv")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_amount_fcfa")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-navy/5">
                        {/* Medical Exam */}
                        <tr>
                          <td className="py-2 pr-2">
                            <p className="font-medium text-navy">{tDoc("ee_medical_title")}</p>
                            <p className="text-[10px] text-navy/60">
                              {calculations.isFamily 
                                ? tDoc("ee_medical_desc_family", { adults: calculations.adultsMedicalUSD, kids: calculations.kids0_10USD + calculations.kids11_14USD + calculations.kids15PlusUSD })
                                : tDoc("ee_medical_desc_single")}
                            </p>
                          </td>
                          <td className="py-2 text-center font-mono">{calculations.medicalTotalUSD} $USD</td>
                          <td className="py-2 text-right text-[11px] text-navy/60">~ {calculations.medicalTotalUSD} USD</td>
                          <td className="py-2 text-right font-mono font-semibold">{formatNumber(calculations.medicalTotalFCFA)}</td>
                        </tr>

                        {/* IRCC Fees */}
                        {calculations.irccFeesBreakdown.map((fee, idx) => {
                          let labelText = fee.label;
                          if (docLang === "en") {
                            if (labelText.includes("Requérent principal")) {
                              labelText = labelText.replace("Requérent principal (Traitement + Droit de résidence permanente)", "Principal Applicant (Processing + Right of Permanent Residence Fee)");
                            } else if (labelText.includes("Données biométriques")) {
                              labelText = labelText.replace("Données biométriques", "Biometrics (Fingerprints & Photo)");
                            } else if (labelText.includes("Conjoint")) {
                              labelText = labelText.replace("Conjoint (Traitement + Droit de résidence permanente)", "Spouse / Partner (Processing + RPRF)");
                            } else if (labelText.includes("Enfant")) {
                              labelText = labelText.replace("Enfant(s) à charge", "Dependent Child(ren)");
                            }
                          }

                          return (
                            <tr key={idx}>
                              <td className="py-2 pr-2">
                                <p className="font-medium text-navy">{labelText}</p>
                                <p className="text-[10px] text-navy/60">{tDoc("ee_ircc_desc")}</p>
                              </td>
                              <td className="py-2 text-center font-mono">{fee.cad} $CAD</td>
                              <td className="py-2 text-right text-[11px] text-navy/60">~ {fee.cad} CAD</td>
                              <td className="py-2 text-right font-mono font-semibold">{formatNumber(Math.round(fee.cad * formData.cad_rate))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* SECTION 3: HONORAIRES JNIC & OPTIONS */}
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-redcanada border-b border-navy/10 pb-1.5 mb-3 flex items-center gap-1.5">
                      <Briefcase size={14} />
                      {tDoc("ee_sec3_title")}
                    </h3>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-navy/50 text-[10px] uppercase border-b border-navy/8">
                          <th className="text-left pb-1 font-semibold">{tDoc("col_service")}</th>
                          <th className="text-center pb-1 font-semibold">{tDoc("col_status")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_conditions")}</th>
                          <th className="text-right pb-1 font-semibold">{tDoc("col_amount_fcfa")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-navy/5">
                        <tr>
                          <td className="py-2 pr-2">
                            <p className="font-semibold text-navy">{tDoc("ee_jnic_fees_title")}</p>
                            <p className="text-[10px] text-navy/60">{tDoc("ee_jnic_fees_desc")}</p>
                          </td>
                          <td className="py-2 text-center">
                            <span className="text-[9px] font-bold bg-navy/10 text-navy px-2 py-0.5 rounded">
                              {tDoc("included_tag")}
                            </span>
                          </td>
                          <td className="py-2 text-right text-[10px] text-navy/60">
                            {calculations.isFamily ? tDoc("formula_family") : tDoc("formula_single")}
                          </td>
                          <td className="py-2 text-right font-mono font-bold text-navy">{formatNumber(calculations.jnicHonorairesFCFA)}</td>
                        </tr>

                        {formData.include_work_exp_assistance && (
                          <tr>
                            <td className="py-2 pr-2">
                              <p className="font-medium text-navy">{tDoc("ee_work_exp_title")}</p>
                              <p className="text-[10px] text-navy/60">{tDoc("ee_work_exp_desc")}</p>
                            </td>
                            <td className="py-2 text-center">
                              <span className="text-[9px] font-medium bg-redcanada/10 text-redcanada px-1.5 py-0.5 rounded">
                                {tDoc("option_tag")}
                              </span>
                            </td>
                            <td className="py-2 text-right text-[10px] text-navy/60">{docLang === "en" ? "Flat-rate" : "Forfaitaire"}</td>
                            <td className="py-2 text-right font-mono font-semibold">{formatNumber(calculations.workExpAssistanceFCFA)}</td>
                          </tr>
                        )}

                        {formData.include_funds_proof_assistance && calculations.proofOfFundsAssistanceFCFA > 0 && (
                          <tr>
                            <td className="py-2 pr-2">
                              <p className="font-medium text-navy">{tDoc("ee_funds_title")}</p>
                              <p className="text-[10px] text-navy/60">{tDoc("ee_funds_desc", { amount: formatNumber(calculations.settlementFundsCAD) })}</p>
                            </td>
                            <td className="py-2 text-center">
                              <span className="text-[9px] font-medium bg-redcanada/10 text-redcanada px-1.5 py-0.5 rounded">
                                {docLang === "en" ? "Option 7%" : "Option 7%"}
                              </span>
                            </td>
                            <td className="py-2 text-right text-[10px] text-navy/60">{docLang === "en" ? "Calculated on required funds" : "Calculé sur fonds requis"}</td>
                            <td className="py-2 text-right font-mono font-semibold">{formatNumber(calculations.proofOfFundsAssistanceFCFA)}</td>
                          </tr>
                        )}

                        {formData.include_contingency && (
                          <tr>
                            <td className="py-2 pr-2">
                              <p className="font-medium text-navy">{tDoc("ee_contingency_title")}</p>
                              <p className="text-[10px] text-navy/60">{tDoc("ee_contingency_desc")}</p>
                            </td>
                            <td className="py-2 text-center">
                              <span className="text-[9px] font-medium bg-cream text-navy/70 px-1.5 py-0.5 rounded">
                                {docLang === "en" ? "Reserve" : "Réserve"}
                              </span>
                            </td>
                            <td className="py-2 text-right text-[10px] text-navy/60">{tDoc("advised_reserve")}</td>
                            <td className="py-2 text-right font-mono font-semibold">{formatNumber(calculations.contingencyFCFA)}</td>
                          </tr>
                        )}

                        {/* LIGNE RABAIS / REMISE COMMERCIALE DÉDUITE */}
                        {calculations.hasDiscount && (
                          <tr className="bg-emerald-50/70 border-t border-b border-emerald-200">
                            <td className="py-2.5 pr-2">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] bg-emerald-600 text-white font-bold px-1.5 py-0.5 rounded uppercase">
                                  {tDoc("discount_badge")}
                                </span>
                                <p className="font-bold text-emerald-950">
                                  {formData.discount_reason || (docLang === "en" ? "Commercial Discount" : "Remise commerciale")}
                                </p>
                              </div>
                              <p className="text-[10px] text-emerald-800">{tDoc("discount_sub")}</p>
                            </td>
                            <td className="py-2.5 text-center">
                              <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                                {tDoc("discount_deducted")}
                              </span>
                            </td>
                            <td className="py-2.5 text-right text-[10px] text-emerald-800 font-medium">{tDoc("discount_courtesy")}</td>
                            <td className="py-2.5 text-right font-mono font-bold text-emerald-700">
                              - {formatNumber(calculations.discountAmountFCFA)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* MODALITÉS DE PAIEMENT DES HONORAIRES JNIC */}
                    <div className="mt-3 pt-3 border-t border-navy/10 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold text-navy flex items-center gap-1.5">
                          <CreditCard size={13} className="text-redcanada" />
                          {tDoc("schedule_title")}
                        </p>
                        <span className="text-[10px] font-semibold text-navy/60">
                          {tDoc("total_fees_label")} {formatNumber(calculations.jnicHonorairesFCFA)} FCFA
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-cream/40 p-2.5 rounded-lg border border-navy/10 text-center">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-navy/60 block">{tDoc("tranche1_title")}</span>
                          <p className="font-mono text-xs font-bold text-navy mt-0.5">{formatNumber(calculations.jnicTranche1FCFA)} FCFA</p>
                          <p className="text-[9px] text-navy/70 mt-1 leading-tight">{tDoc("tranche1_desc")}</p>
                        </div>

                        <div className="bg-cream/40 p-2.5 rounded-lg border border-navy/10 text-center">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-navy/60 block">{tDoc("tranche2_title")}</span>
                          <p className="font-mono text-xs font-bold text-navy mt-0.5">{formatNumber(calculations.jnicTranche2FCFA)} FCFA</p>
                          <p className="text-[9px] text-navy/70 mt-1 leading-tight">{tDoc("tranche2_desc")}</p>
                        </div>

                        <div className="bg-navy/5 p-2.5 rounded-lg border border-redcanada/30 text-center">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-redcanada block">{tDoc("tranche3_title")}</span>
                          <p className="font-mono text-xs font-bold text-redcanada mt-0.5">{formatNumber(calculations.jnicTranche3FCFA)} FCFA</p>
                          <p className="text-[9px] text-navy/70 mt-1 leading-tight">{tDoc("tranche3_desc")}</p>
                        </div>
                      </div>

                      {/* CLAUSE DE REMBOURSEMENT EN CAS DE REFUS */}
                      <div className="bg-emerald-50/80 border border-emerald-200 rounded-lg p-2.5 flex items-start gap-2 text-[10px] text-emerald-950">
                        <ShieldCheck size={15} className="text-emerald-700 shrink-0 mt-0.5" />
                        <div>
                          <strong className="text-emerald-900 font-semibold">{tDoc("refusal_title")} </strong>
                          {tDoc("refusal_body", {
                            remb: formatNumber(calculations.jnicRemboursementRefusFCFA),
                            verse: formatNumber(calculations.jnicTotalVerseAvantVisaFCFA),
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* GRAND TOTAL SUMMARY BLOCK */}
              <div className="bg-navy text-alabaster p-5 rounded-xl border border-navy shadow-sm">
                {calculations.hasDiscount && (
                  <div className="mb-3 pb-3 border-b border-white/15 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-emerald-500/20 text-emerald-300 font-bold px-2 py-0.5 rounded border border-emerald-400/30">
                        {tDoc("discount_applied")}
                      </span>
                      <span className="text-alabaster/80 font-medium">
                        {formData.discount_reason || (docLang === "en" ? "Commercial Discount" : "Remise commerciale")} :
                      </span>
                    </div>
                    <div className="flex items-center gap-3 font-mono">
                      <span className="text-alabaster/60 line-through text-[11px]">
                        {tDoc("gross_subtotal")} {formatNumber(calculations.subtotalFCFA)} FCFA
                      </span>
                      <span className="text-emerald-400 font-bold">
                        - {formatNumber(calculations.discountAmountFCFA)} FCFA
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-redcanada font-semibold">
                      {docType === "receipt" ? tDoc("net_total_receipt") : tDoc("net_total_estimate")}
                    </p>
                    <p className="text-xs text-alabaster/70 mt-0.5">
                      {calculations.hasDiscount 
                        ? tDoc("total_desc_with_discount")
                        : tDoc("total_desc_regular")}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="font-serif text-3xl font-bold tracking-tight text-white font-mono">
                      {formatNumber(calculations.grandTotalFCFA)} <span className="text-lg font-sans font-normal text-redcanada">FCFA</span>
                    </p>
                    <p className="text-xs text-alabaster/70 font-mono mt-0.5">
                      {tDoc("equiv_label")} ~ <strong className="text-white">{formatNumber(calculations.grandTotalCAD)} $CAD</strong> · ~ {formatNumber(calculations.grandTotalUSD)} $USD
                    </p>
                  </div>
                </div>
              </div>

              {/* SPECIAL NOTICE 1: PREUVE DE FONDS IRCC OBLIGATOIRE */}
              {formData.procedure_type !== "work_contract" && (
                <div className="bg-cream/70 border border-navy/12 rounded-xl p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs font-bold text-navy">
                      <AlertCircle size={15} className="text-redcanada shrink-0" />
                      <span>{tDoc("ircc_funds_title")}</span>
                    </div>
                    <span className="text-[10px] font-semibold bg-white border border-navy/15 px-2 py-0.5 rounded text-navy">
                      {tDoc("ircc_funds_badge")}
                    </span>
                  </div>
                  <p className="text-[11px] text-navy/80 leading-relaxed">
                    {tDoc("ircc_funds_body", { total: calculations.totalFamilyMembers })}
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-navy/10 flex items-center justify-between">
                    <span className="text-xs font-medium text-navy/70">{tDoc("ircc_funds_label")}</span>
                    <span className="font-mono text-base font-bold text-redcanada">
                      {formatNumber(calculations.settlementFundsCAD)} $CAD <span className="text-xs font-normal text-navy/60">(~ {formatNumber(calculations.settlementFundsFCFA)} FCFA)</span>
                    </span>
                  </div>
                  <p className="text-[10px] text-navy/60 italic">
                    {tDoc("ircc_funds_disclaimer")}
                  </p>
                </div>
              )}

              {/* SPECIAL NOTICE 2: POST-ARRIVAL SERVICES (Optionnel) */}
              {formData.include_post_arrival_option && (
                <div className="bg-forest/5 border border-forest/20 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-bold text-forest">
                      <Plane size={15} className="shrink-0" />
                      <span>{tDoc("post_arrival_title")}</span>
                    </div>
                    <span className="font-mono font-bold text-forest text-xs">
                      {formatNumber(calculations.postArrivalFCFA)} FCFA
                    </span>
                  </div>
                  <p className="text-[11px] text-navy/80 leading-relaxed">
                    {tDoc("post_arrival_body")}
                  </p>
                  <p className="text-[10px] text-navy/50 italic">
                    {tDoc("post_arrival_disclaimer")}
                  </p>
                </div>
              )}

              {/* SPECIAL NOTICE 3: LEGAL DISCLOSURE */}
              <div className="border-t border-navy/10 pt-4 space-y-2.5 text-[10px] text-navy/70 leading-relaxed bg-cream/30 p-4 rounded-xl">
                <p className="font-bold text-navy text-[11px] uppercase tracking-wider">
                  {calculations.isWorkContract 
                    ? tDoc("legal_wc_title")
                    : tDoc("legal_ee_title")}
                </p>
                {calculations.isWorkContract ? (
                  <>
                    <p>
                      {tDoc("legal_wc_intro", { total: formatNumber(calculations.grandTotalFCFA) })}
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 list-disc pl-4 text-[10px]">
                      <li>{tDoc("legal_wc_p1", { sector: calculations.workContractCompanySector || (docLang === "en" ? "Nylon paper manufacturing company" : "Entreprise de fabrication de papier nylon") })}</li>
                      <li>{tDoc("legal_wc_p2", { processing: formatNumber(calculations.workContractProcessingFCFA) })}</li>
                      <li>{tDoc("legal_wc_p3", { proof_funds: formatNumber(calculations.workContractProofFundsFCFA) })}</li>
                      <li>{tDoc("legal_wc_p4")}</li>
                      <li><strong>{tDoc("legal_wc_p5")}</strong></li>
                      <li>{tDoc("legal_wc_p6")}</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p>
                      {tDoc("legal_ee_intro", { fees: formatNumber(calculations.jnicHonorairesFCFA) })}
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 list-disc pl-4 text-[10px]">
                      <li>{tDoc("legal_ee_p1")}</li>
                      <li>{tDoc("legal_ee_p2")}</li>
                      <li>{tDoc("legal_ee_p3")}</li>
                      <li>{tDoc("legal_ee_p4")}</li>
                      <li>{tDoc("legal_ee_p5")}</li>
                    </ul>
                    <div className="bg-white/80 p-2.5 rounded border border-navy/8 mt-2">
                      <p className="font-semibold text-navy">{tDoc("french_test_notice_title")}</p>
                      <p className="text-navy/70">
                        {tDoc("french_test_notice_body")}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* DOCUMENT SIGNATURES & STAMPS */}
              <div className="border-t border-navy/10 pt-5 mt-4 grid grid-cols-2 gap-4">
                <div className="border border-navy/15 rounded-xl p-3 bg-cream/30 text-center">
                  <p className="text-[10px] uppercase font-bold text-navy/60">
                    {docType === "receipt" ? tDoc("signature_applicant_receipt") : tDoc("signature_applicant")}
                  </p>
                  <p className="text-xs font-semibold text-navy mt-1">
                    {formData.client_name || (docLang === "en" ? "Applicant" : "Client")}
                  </p>
                  <div className="h-12 border-b border-dashed border-navy/25 mx-4 my-2"></div>
                  <p className="text-[9px] text-navy/40 italic">{tDoc("date_and_signature")}</p>
                </div>

                <div className="border border-navy/15 rounded-xl p-3 bg-cream/30 text-center relative overflow-hidden">
                  <p className="text-[10px] uppercase font-bold text-navy/60">
                    {docType === "receipt" ? tDoc("signature_accounting") : tDoc("signature_agency")}
                  </p>
                  <p className="text-xs font-semibold text-navy mt-1">
                    JN Immigration Consulting (JNIC)
                  </p>
                  <div className="h-12 flex items-center justify-center border-b border-dashed border-navy/25 mx-4 my-2">
                    <span className="text-[10px] font-mono font-bold text-redcanada border border-redcanada/40 px-2 py-0.5 rounded rotate-[-4deg] bg-redcanada/5">
                      🍁 JNIC SEAL · VERIFIED
                    </span>
                  </div>
                  <p className="text-[9px] text-navy/40 italic">{tDoc("authorized_signature")}</p>
                </div>
              </div>

              {/* DOCUMENT FOOTER SIGNATURE */}
              <div className="border-t border-navy/10 pt-4 flex items-center justify-between text-[10px] text-navy/50">
                <p>JN Immigration Consulting (JNIC) · Douala - Montréal</p>
                <p>{tDoc("generated_on")} {formData.date}</p>
              </div>

            </div>

          </div>

          {/* ACTION TOOLBAR UNDER PREVIEW */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-navy/8">
            <div className="flex items-center gap-2 text-xs text-navy/60">
              <CheckCircle2 size={14} className="text-forest" />
              <span>
                {docLang === "en"
                  ? (docType === "receipt" ? "Official receipt ready to send or print." : "Official estimate ready to share with applicant.")
                  : (docType === "receipt" ? "Reçu officiel de paiement prêt à être remis ou imprimé." : "Devis officiel prêt à être transmis au candidat.")}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopySummary}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-navy/15 text-navy text-xs hover:bg-cream transition"
              >
                <Copy size={13} />
                {copiedSuccess ? (docLang === "en" ? "Copied!" : "Copié !") : (docLang === "en" ? "Copy Text" : "Copier")}
              </button>

              <button
                type="button"
                onClick={handleOpenEmailModal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs hover:bg-emerald-700 transition"
              >
                <Mail size={13} />
                {docLang === "en" ? "Send by Email" : "Envoyer par Email"}
              </button>

              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-navy text-alabaster text-xs hover:bg-redcanada transition"
              >
                <Printer size={13} />
                {docLang === "en" ? "Print" : "Imprimer"}
              </button>
            </div>
          </div>

        </div>

      </div>

      {/* EMAIL SENDING MODAL */}
      {emailModalOpen && (
        <div className="fixed inset-0 z-[100] bg-navy/70 backdrop-blur-sm p-4 flex items-center justify-center animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-navy/10 max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            
            {/* Modal Header */}
            <div className="p-5 border-b border-navy/8 flex items-center justify-between bg-cream/40">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">
                  <Mail size={16} />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-navy">
                    {docLang === "en"
                      ? (docType === "receipt" ? "Email Payment Receipt" : "Email Budget Estimate")
                      : (docType === "receipt" ? "Envoyer le Reçu par Courriel" : "Envoyer le Devis par Courriel")}
                  </h3>
                  <p className="text-[11px] text-navy/60">Expédié depuis info@jnic.org via Zoho Mail</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Language switcher inside email modal */}
                <div className="flex items-center bg-white border border-navy/10 rounded-lg p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      handleSetDocLang("fr");
                      setEmailForm((prev) => ({
                        ...prev,
                        subject: docType === "receipt"
                          ? `Reçu de Paiement Officiel - JNIC Canada (Réf: ${formData.receipt_number || formData.estimate_id})`
                          : `Devis Estimatif Détaillé - Immigration Canada (Réf: ${formData.estimate_id})`,
                        body: `Bonjour ${formData.client_name || ""},\n\nVeuillez trouver ci-dessous votre ${docType === "receipt" ? "reçu de paiement officiel" : "budget estimatif personnalisé"} pour votre projet d'immigration vers le Canada avec l'accompagnement de JN Immigration Consulting.\n\n${generateDocumentSummaryText({ formData, calculations, docType, docLang: "fr", formatNumber })}\n\nNous restons à votre entière disposition pour toute question.\n\nCordialement,\nL'équipe JN Immigration Consulting\ninfo@jnic.org`,
                      }));
                    }}
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold transition ${docLang === "fr" ? "bg-navy text-white" : "text-navy/60"}`}
                  >
                    FR
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleSetDocLang("en");
                      setEmailForm((prev) => ({
                        ...prev,
                        subject: docType === "receipt"
                          ? `Official Payment Receipt - JNIC Canada (Ref: ${formData.receipt_number || formData.estimate_id})`
                          : `Official Immigration Budget Estimate - JNIC Canada (Ref: ${formData.estimate_id})`,
                        body: `Hello ${formData.client_name || ""},\n\nPlease find attached below your official ${docType === "receipt" ? "payment receipt" : "budget estimate"} for your Canadian immigration file with JN Immigration Consulting.\n\n${generateDocumentSummaryText({ formData, calculations, docType, docLang: "en", formatNumber })}\n\nPlease feel free to contact us should you have any questions.\n\nWarm regards,\nThe JN Immigration Consulting Team\ninfo@jnic.org`,
                      }));
                    }}
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold transition ${docLang === "en" ? "bg-navy text-white" : "text-navy/60"}`}
                  >
                    EN
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setEmailModalOpen(false)}
                  className="text-navy/40 hover:text-navy p-1 rounded-lg hover:bg-cream"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {emailSuccess && (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
                  <CheckCircle2 size={16} className="shrink-0" />
                  <span>
                    {docLang === "en"
                      ? "The document has been successfully sent to the applicant!"
                      : "Le document a été envoyé avec succès au client !"}
                  </span>
                </div>
              )}

              {emailError && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs flex items-center gap-2">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{emailError}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-navy mb-1">
                  {docLang === "en" ? "Recipient (Applicant Email) *" : "Destinataire (Email du client) *"}
                </label>
                <input
                  type="email"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  placeholder="client@domaine.com"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 focus:outline-none focus:border-redcanada"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-navy mb-1">
                  {docLang === "en" ? "Email Subject *" : "Objet du courriel *"}
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-navy/15 focus:outline-none focus:border-redcanada font-medium text-navy"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-navy mb-1">
                  {docLang === "en" ? "Formatted Message & Document" : "Message & Document Formaté"}
                </label>
                <textarea
                  rows={9}
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  className="w-full p-3.5 text-xs rounded-xl border border-navy/15 focus:outline-none focus:border-redcanada leading-relaxed font-mono text-navy/80 resize-y"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-navy/8 bg-cream/30 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleOpenMailto}
                  className="inline-flex items-center gap-1 text-[11px] text-navy/60 hover:text-navy underline"
                  title={docLang === "en" ? "Open in Outlook or local email client" : "Ouvrir dans Outlook ou votre application de messagerie locale"}
                >
                  <ExternalLink size={12} />
                  {docLang === "en" ? "Open in Mail App" : "Ouvrir dans mon client Mail"}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEmailModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-navy/70 hover:bg-cream"
                >
                  {docLang === "en" ? "Cancel" : "Annuler"}
                </button>
                <button
                  type="button"
                  onClick={handleSendEmail}
                  disabled={emailSending}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-navy text-white text-xs font-medium hover:bg-redcanada disabled:opacity-50 transition shadow-sm"
                >
                  {emailSending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                  {emailSending 
                    ? (docLang === "en" ? "Sending..." : "Envoi en cours...") 
                    : (docLang === "en" ? (docType === "receipt" ? "Send Receipt" : "Send Estimate") : (docType === "receipt" ? "Envoyer le reçu" : "Envoyer le devis"))}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
